import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, ListGroup, Row, Spinner, Table, Tab, Tabs } from 'react-bootstrap';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { useArea } from '../context/AreaContext';
import { useModal } from '../context/ModalContext';
import ProjectsAlertStack from '../components/projects/ProjectsAlertStack';
import ProjectsKpiMetricGrid from '../components/projects/ProjectsKpiMetricGrid';
import ProjectsStructuredFilters from '../components/projects/ProjectsStructuredFilters';
import ProjectAreaLayout from '../components/projects/ProjectAreaLayout';
import ProjectEmptyState from '../components/projects/ProjectEmptyState';
import ProjectLoadingState from '../components/projects/ProjectLoadingState';
import ProjectPageHeader from '../components/projects/ProjectPageHeader';
import ProjectSectionCard from '../components/projects/ProjectSectionCard';
import {
  JiraProjectIssueRow,
  ProjectGlobalView,
  ProjectGlobalViewsExportPayload,
  ProjectGlobalViewsImportPayload,
  ProjectGlobalViewsImportResponse,
  ProjectCostCalculationResponse,
  ProjectsJiraColumnOption,
  ProjectsJiraSearchResponse,
  ProjectsNlQueryResponse,
} from '../types';
import {
  COMPRAS_E_COMPARATIVO_PRESET_ID,
  EMPTY_STRUCTURED_FILTERS,
  PMO_CARTEIRA_FIELD_DEFAULT,
  PMO_PRESET_SPECS,
  PmoCarteiraGroup,
  PmoPresetSpec,
  PmoStructuredFiltersState,
  buildPresetViews,
  canViewPreset,
  pmoComparativoDefaultFilters,
  reconcileComprasEPresetKpis,
  resolveProjectsAccessLevel,
} from './projects/pmoSpecConfig';
import {
  computeCarteiraTotals,
  executiveRiskLevel,
  filterPmoRows,
  hasActivePmoRowFilters,
  hasPartialPeriodInput,
  isDeliveryOutOfSchedule,
  isDoneStatusLabel,
  sanitizeStructuredFilters,
  toEffectivePmoFilters,
  validatePmoRowFilters,
} from './projects/pmoRowFilters';
import {
  buildIssueCostMapsFromAnalysis,
  buildJiraIssueByKey,
  decorateIssueRowWithCostMaps,
} from './projects/projectCostDecoration';
import {
  calculateProjectCostsBatched,
  calculateComprasEExportHours,
  emptyProjectCostResponse,
} from './projects/projectCostCalculate';
import {
  VISIBLE_COST_BATCH_SIZE,
  VISIBLE_COST_BATCH_TIMEOUT_MS,
  VISIBLE_COST_MAX_SINGLE_RETRIES,
  VISIBLE_COST_SINGLE_TIMEOUT_MS,
  FULL_WORKLOG_PERIOD_BATCH_SIZE,
  FULL_WORKLOG_PERIOD_BATCH_TIMEOUT_MS,
  FULL_WORKLOG_PERIOD_EXPORT_TIMEOUT_MS,
  FULL_WORKLOG_PERIOD_MAX_SINGLE_RETRIES,
  TABLE_COST_PIPELINE_DEFAULTS,
  PERIOD_EXPORT_PIPELINE_DEFAULTS,
  buildFullCostScopeKey,
  buildVisibleCostScopeKey,
  normalizeIssueKeysForCost,
} from './projects/projectCostScope';
import {
  hasCompletePmoWorklogPeriod,
  pmoPeriodToWorklogIsoDates,
} from './projects/pmoWorklogPeriodBounds';
import {
  filterRowsWithWorklogInPeriod,
  isExportWorklogPeriodScope,
  isPeriodWorklogKpiId,
  toPeriodReportScopeApi,
  shouldHoldExecutiveCostUntilExportSettles,
  PeriodReportScopeApi,
} from './projects/pmoReportScope';
import {
  commitKpiTitle as commitKpiTitleValue,
  draftKpiTitle,
  extractPeriodKpiIds,
  reorderKpiConfigs,
  toggleKpiConfig,
} from './projects/pmoKpiConfig';
import './ProjectsDashboard.css';

const formatDateBR = (iso?: string | null): string => {
  if (!iso) return '—';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (dateOnly) {
    return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return iso;
  }
  return d.toLocaleDateString('pt-BR');
};

const formatHoursFromSeconds = (sec?: number | null): string => {
  if (sec == null || sec <= 0) return '—';
  const h = sec / 3600;
  return h.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const formatPercent = (value?: number | null): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}%`;
};

const formatHhConsumptionPercent = (hhReal?: number | null, hhVendido?: number | null): string => {
  const sold = Number(hhVendido || 0);
  const real = Number(hhReal || 0);
  if (!Number.isFinite(sold) || sold <= 0 || !Number.isFinite(real)) return '—';
  const consumption = (real / sold) * 100;
  return `${consumption.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

const formatCurrencyFromCents = (cents?: number | null): string => {
  if (cents == null) return '—';
  return (Number(cents) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatCurrencyCompactFromCents = (cents?: number | null): string => {
  if (cents == null) return '—';
  const value = Number(cents) / 100;
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mi`;
  }
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
type CoreColumnId =
  | 'project'
  | 'issue_type'
  | 'go_live_planned'
  | 'go_live_real'
  | 'updated'
  | 'hh_vendido_seconds'
  | 'hh_real_seconds'
  | 'created'
  | 'duedate'
  | 'status';

type SortColumn = string;
type ProjectsColumnGroupId = 'jira_core' | 'cost_computed' | 'jira_custom';
const LOCAL_COST_COLUMN_ID = '__cost_configured_cents';
const SALE_VALUE_COLUMN_ID = '__sale_value_cents';
const ESTIMATED_COST_COLUMN_ID = '__estimated_cost_cents';
const OWN_HOURS_COLUMN_ID = '__own_hours';
const THIRD_HOURS_COLUMN_ID = '__third_party_hours';
const OWN_COST_COLUMN_ID = '__own_cost_cents';
const THIRD_COST_COLUMN_ID = '__third_party_cost_cents';
const ISSUE_COST_COLUMN_ID = '__issue_cost_cents';
const COMMERCIAL_MARGIN_COLUMN_ID = '__commercial_margin_cents';
const COMMERCIAL_MARGIN_PCT_COLUMN_ID = '__commercial_margin_pct';
const SALE_MARGIN_COLUMN_ID = '__sale_margin_cents';
const SALE_MARGIN_PCT_COLUMN_ID = '__sale_margin_pct';
const POST_COST_MARGIN_PCT_COLUMN_ID = '__post_cost_margin_pct';
const DEPRECATED_COLUMN_IDS = new Set<string>([POST_COST_MARGIN_PCT_COLUMN_ID]);

const COMPUTED_COLUMN_LABELS: Record<string, string> = {
  [LOCAL_COST_COLUMN_ID]: 'Custo total do projeto (contexto)',
  [SALE_VALUE_COLUMN_ID]: 'Valor de Venda (R$)',
  [ESTIMATED_COST_COLUMN_ID]: 'Custo Previsto Interno (R$)',
  [OWN_HOURS_COLUMN_ID]: 'HH próprio (realizado)',
  [THIRD_HOURS_COLUMN_ID]: 'HH terceiro (realizado)',
  [COMMERCIAL_MARGIN_COLUMN_ID]: 'Margem na Venda (Comercial | R$)',
  [COMMERCIAL_MARGIN_PCT_COLUMN_ID]: '% Margem na Venda (Comercial)',
  [SALE_MARGIN_COLUMN_ID]: 'Margem na Venda (Pós-custo | R$)',
  [SALE_MARGIN_PCT_COLUMN_ID]: '% Margem na Venda (Pós-custo)',
  [OWN_COST_COLUMN_ID]: 'Custo R$ Próprio',
  [THIRD_COST_COLUMN_ID]: 'Custo R$ Terceiro',
  [ISSUE_COST_COLUMN_ID]: 'Custo Total',
};

const COMPUTED_COLUMN_IDS = Object.keys(COMPUTED_COLUMN_LABELS);
const COMPUTED_COLUMN_IDS_SET = new Set(COMPUTED_COLUMN_IDS);

type CostCellKind = 'currency' | 'percent' | 'hours';

const CostValueSkeleton: React.FC<{ kind?: CostCellKind }> = ({ kind = 'currency' }) => (
  <span
    className={`projects-cost-skeleton projects-cost-skeleton--${kind}`}
    aria-busy="true"
    aria-label="Calculando custos"
    title="Calculando custos…"
  />
);

const renderComputedCostCellValue = (
  columnId: string,
  rawValue: unknown,
  waiting: boolean,
): React.ReactNode => {
  const isPct = [COMMERCIAL_MARGIN_PCT_COLUMN_ID, SALE_MARGIN_PCT_COLUMN_ID].includes(columnId);
  const isHours = [OWN_HOURS_COLUMN_ID, THIRD_HOURS_COLUMN_ID].includes(columnId);
  const kind: CostCellKind = isPct ? 'percent' : isHours ? 'hours' : 'currency';

  if (waiting) {
    return <CostValueSkeleton kind={kind} />;
  }

  const value = rawValue == null ? null : Number(rawValue);
  if (value == null || Number.isNaN(value)) {
    return (
      <span className="projects-cost-empty" title="Sem valor calculado">
        —
      </span>
    );
  }
  if (isPct) {
    return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  }
  if (isHours) {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }
  return formatCurrencyFromCents(value);
};
const COLUMN_GROUP_ORDER: ProjectsColumnGroupId[] = ['jira_core', 'cost_computed', 'jira_custom'];
const COLUMN_GROUP_META: Record<ProjectsColumnGroupId, { label: string; hint: string }> = {
  jira_core: {
    label: 'Colunas Jira (base)',
    hint: 'Campos padrão mapeados do Jira.',
  },
  cost_computed: {
    label: 'Colunas de custos e margens',
    hint: 'Campos calculados pelo módulo de custos.',
  },
  jira_custom: {
    label: 'Colunas Jira (customizadas)',
    hint: 'Campos adicionais/customfields do Jira.',
  },
};

const COLUMN_DEFAULT_WIDTHS: Record<string, number> = {
  project: 340,
  issue_type: 110,
  go_live_planned: 130,
  go_live_real: 130,
  updated: 120,
  created: 120,
  duedate: 120,
  hh_vendido_seconds: 125,
  hh_real_seconds: 125,
  status: 150,
  [LOCAL_COST_COLUMN_ID]: 170,
  [SALE_VALUE_COLUMN_ID]: 150,
  [ESTIMATED_COST_COLUMN_ID]: 170,
  [OWN_HOURS_COLUMN_ID]: 140,
  [THIRD_HOURS_COLUMN_ID]: 145,
  [COMMERCIAL_MARGIN_COLUMN_ID]: 170,
  [COMMERCIAL_MARGIN_PCT_COLUMN_ID]: 140,
  [SALE_MARGIN_COLUMN_ID]: 160,
  [OWN_COST_COLUMN_ID]: 140,
  [THIRD_COST_COLUMN_ID]: 145,
  [ISSUE_COST_COLUMN_ID]: 150,
  [SALE_MARGIN_PCT_COLUMN_ID]: 130,
  [POST_COST_MARGIN_PCT_COLUMN_ID]: 170,
  customfield_11263: 280,
};

const getDefaultColumnWidth = (columnId: string): number => {
  return COLUMN_DEFAULT_WIDTHS[columnId] || MIN_COLUMN_WIDTH_PX;
};

/** Garante Valor de Venda (__sale_value_cents) sempre antes de Fase (status). */
const ensureSaleValueBeforeStatus = (columns: string[]): string[] => {
  const saleIdx = columns.indexOf(SALE_VALUE_COLUMN_ID);
  const statusIdx = columns.indexOf('status');
  if (saleIdx < 0 || statusIdx < 0 || saleIdx < statusIdx) return columns;
  const next = columns.filter((id) => id !== SALE_VALUE_COLUMN_ID);
  const newStatusIdx = next.indexOf('status');
  if (newStatusIdx < 0) return next;
  next.splice(newStatusIdx, 0, SALE_VALUE_COLUMN_ID);
  return next;
};

const normalizeSelectedColumns = (columns: string[]): string[] => {
  const normalized = columns
    .map((value) => String(value || '').trim())
    .filter((value) => value && !DEPRECATED_COLUMN_IDS.has(value));
  const unique = Array.from(new Set(normalized));
  const ordered = ensureSaleValueBeforeStatus(unique);
  return ordered.length ? ordered : [...CORE_DEFAULT_COLUMNS];
};

const areSameColumns = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((item, idx) => item === b[idx]);
};

const extractKpiIds = (kpis: ProjectsKpiConfig[]): string[] => {
  return kpis.map((item) => String(item.id || '').trim()).filter(Boolean);
};

const hasExecutiveKpi = (kpiIds: string[]): boolean => {
  return kpiIds.some((id) => EXECUTIVE_KPI_IDS.has(id));
};

const normalizeMatchText = (value: string): string => (
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
);

const normalizeFieldLabel = (value: string): string => (
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
);

const CONTINUITY_VIEW_NAME_BASE = 'MELHORIAS DE CONTINUIDADE OPERACIONAL 2º. TRI — Projetos em atendimento';
const CONTINUITY_FINANCE_VIEW_NAME = 'MELHORIAS DE CONTINUIDADE OPERACIONAL 2º. TRI — Financeiro e execução';
const INNOVATION_CONCLUDED_VIEW_NAME = 'MELHORIAS E INOVAÇÕES DO 1º. TRI — Projetos concluídos';

const resolveColumnGroup = (columnId: string): ProjectsColumnGroupId => {
  if (COMPUTED_COLUMN_IDS_SET.has(columnId)) return 'cost_computed';
  if (CORE_COLUMN_IDS_SET.has(columnId as CoreColumnId)) return 'jira_core';
  return 'jira_custom';
};

const parseDateSortable = (value?: string | null): number => {
  if (!value) return Number.NEGATIVE_INFINITY;
  const normalized = value.trim();
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized);
  if (dateOnly) {
    return Date.UTC(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const ts = Date.parse(normalized);
  if (!Number.isNaN(ts)) return ts;
  const fallback = /^(\d{4})-(\d{2})-(\d{2})/.exec(normalized);
  if (fallback) {
    return Date.UTC(Number(fallback[1]), Number(fallback[2]) - 1, Number(fallback[3]));
  }
  return Number.NEGATIVE_INFINITY;
};

const getCoreSortValue = (row: JiraProjectIssueRow, column: CoreColumnId): string | number => {
  switch (column) {
    case 'project':
      return `${row.key} ${row.summary} ${row.project_key} ${row.project_name}`.toLowerCase();
    case 'issue_type':
      return (row.issue_type || '').toLowerCase();
    case 'go_live_planned':
      return parseDateSortable(row.go_live_planned);
    case 'go_live_real':
      return parseDateSortable(row.go_live_real);
    case 'updated':
      return parseDateSortable(row.updated);
    case 'created':
      return parseDateSortable(row.created);
    case 'duedate':
      return parseDateSortable(row.duedate);
    case 'hh_vendido_seconds':
      return row.hh_vendido_seconds ?? Number.NEGATIVE_INFINITY;
    case 'hh_real_seconds':
      return row.hh_real_seconds ?? Number.NEGATIVE_INFINITY;
    case 'status':
      return (row.status || '').toLowerCase();
    default:
      return '';
  }
};

const phaseVariant = (
  row: JiraProjectIssueRow,
  visibleColumns?: string[],
  ignoredVisibleColumns?: string[],
): string => {
  const risk = executiveRiskLevel(row, { visibleColumns, ignoredVisibleColumns });
  if (risk === 'critico') return 'danger';
  if (risk === 'em_risco') return 'warning';
  if (risk === 'no_prazo') return 'success';
  return 'secondary';
};

/** Legenda das cores do badge de Fase (texto = status Jira; cor = risco executivo). */
const PHASE_COLOR_LEGEND: ReadonlyArray<{ bg: string; label: string; description: string }> = [
  { bg: 'success', label: 'No prazo', description: 'Dentro do prazo e sem indícios de risco' },
  { bg: 'warning', label: 'Em risco', description: 'Atraso iminente, HH perto/acima do limite, fase amarela/vermelha ou campos obrigatórios faltando' },
  { bg: 'danger', label: 'Crítico', description: 'Entrega real depois da data prevista' },
  { bg: 'secondary', label: 'Indefinido', description: 'Sem dados suficientes para classificar' },
];

const phaseRiskTitle = (
  row: JiraProjectIssueRow,
  visibleColumns?: string[],
  ignoredVisibleColumns?: string[],
): string => {
  const risk = executiveRiskLevel(row, { visibleColumns, ignoredVisibleColumns });
  const item = PHASE_COLOR_LEGEND.find((entry) => {
    if (risk === 'critico') return entry.bg === 'danger';
    if (risk === 'em_risco') return entry.bg === 'warning';
    if (risk === 'no_prazo') return entry.bg === 'success';
    return entry.bg === 'secondary';
  });
  if (!item) return 'Classificação de risco da fase';
  return `${item.label}: ${item.description}`;
};

interface ProjectsSavedView {
  id: string;
  backendId?: number;
  name: string;
  context: string;
  jql: string;
  selectedColumns: string[];
  kpiConfigs: ProjectsKpiConfig[];
  // Compatibilidade com versões antigas no localStorage
  kpiCards?: string[];
  columnWidths: Record<string, number>;
  isPreset?: boolean;
  presetSpecId?: string;
  carteiraGroup?: PmoCarteiraGroup;
  accessLevel?: 'np0' | 'np3';
}

const PROJECTS_VIEWS_STORAGE_KEY = 'projects_dashboard_saved_views_v1';
const PROJECTS_VIEWS_SCHEMA_VERSION = 4;
const MIN_COLUMN_WIDTH_PX = 120;
const CORE_DEFAULT_COLUMNS: CoreColumnId[] = ['project', 'go_live_planned', 'go_live_real', 'hh_vendido_seconds', 'hh_real_seconds', 'status'];
const DEFAULT_KPI_CARDS = [
  'total_items',
  'hh_vendido_total',
  'hh_real_total',
  'consumo_hh_percent',
];

const EXECUTIVE_KPI_IDS = new Set<string>([
  'venda_total',
  'venda_a_faturar',
  'hh_a_faturar',
  'margem_na_venda_percentual_total',
  'margem_venda_comercial_total',
  'custo_previsto_total',
  'custo_total',
  'margem_pos_custo_total',
  'ponto_critico_os',
  'ponto_critico_demanda',
  'hh_realizado_total',
  'horas_realizadas_mix',
  'custo_mix',
]);

interface ProjectsKpiOption {
  id: string;
  label: string;
  description: string;
}

interface ProjectsKpiConfig {
  id: string;
  title?: string;
}

const KPI_OPTIONS: ProjectsKpiOption[] = [
  { id: 'total_items', label: 'Projetos em atendimento', description: 'Quantidade total de registros na tabela.' },
  { id: 'hh_vendido_total', label: 'HH Vendido', description: 'Soma do HH vendido estimado.' },
  { id: 'hh_real_total', label: 'HH Real', description: 'Soma do HH real gasto (tempo acumulado no Jira).' },
  { id: 'hh_real_worklog_period', label: 'HH Real (período)', description: 'Soma de apontamentos no De/Até. Com a metodologia Relatório SAP+NSAP (Jira), é a coluna Horas trabalhadas do export — não o HH Real clássico nem as Histórias da tabela.' },
  { id: 'hh_vendido_relatorio', label: 'HH Vendido (com apontamento no período)', description: 'HH vendido das Histórias que tiveram worklog > 0 no intervalo De/Até.' },
  { id: 'consumo_hh_relatorio_percent', label: '% Consumo (período)', description: 'HH Real (período) ÷ HH Vendido (com apontamento no período).' },
  { id: 'consumo_hh_percent', label: '% Consumo HH', description: 'Percentual HH Real / HH Vendido.' },
  { id: 'venda_total', label: 'Venda total', description: 'Receita total no contexto da JQL aplicada.' },
  { id: 'venda_a_faturar', label: 'Valor de venda a faturar', description: 'Estimativa da venda adicional baseada em HH a faturar.' },
  { id: 'hh_a_faturar', label: 'HH a faturar', description: 'Diferença entre HH real e HH vendido no contexto.' },
  { id: 'margem_na_venda_percentual_total', label: 'Margem na venda (%)', description: 'Percentual de margem total sobre a venda.' },
  { id: 'hh_realizado_total', label: 'HH Real', description: 'Horas totais realizadas com base nos worklogs.' },
  { id: 'margem_venda_comercial_total', label: 'Margem venda (comercial)', description: 'Margem estimada comercial no contexto da JQL.' },
  { id: 'custo_previsto_total', label: 'Custo previsto', description: 'Custo previsto interno no contexto da JQL.' },
  { id: 'custo_total', label: 'Custo total', description: 'Custo total (próprio + terceiro) no contexto da JQL.' },
  { id: 'horas_realizadas_mix', label: 'Horas realizadas (próprio/terceiro)', description: 'Distribuição de HH realizado entre próprio e terceiro.' },
  { id: 'custo_mix', label: 'Custo (próprio/terceiro)', description: 'Distribuição percentual do custo entre próprio e terceiro.' },
  { id: 'margem_pos_custo_total', label: 'Margem pós-custo', description: 'Margem total após custos no contexto da JQL.' },
  { id: 'ponto_critico_os', label: 'Ponto crítico (OS)', description: 'Quantidade de OS com margem pós-custo crítica.' },
  { id: 'ponto_critico_demanda', label: 'Ponto crítico (demanda)', description: 'Quantidade crítica por tipo de demanda (ex.: AS/OS).' },
  { id: 'pontos_criticos', label: 'Pontos críticos', description: 'Itens com data de entrega real depois da prevista.' },
  { id: 'fases_no_prazo', label: 'Projetos no prazo', description: 'Itens em atendimento classificados como no prazo.' },
  { id: 'projetos_fora_do_prazo', label: 'Projetos fora do prazo', description: 'Não concluídos com entrega real depois da prevista, ou prevista vencida sem data real.' },
  { id: 'concluidos', label: 'Concluídos', description: 'Itens em status finalizado/concluído.' },
  { id: 'atrasados_due_date', label: 'Atrasados (Data limite)', description: 'Itens com data limite vencida e não concluídos.' },
  { id: 'em_risco', label: 'Em risco', description: 'Itens com fase/status identificado como risco.' },
];

const CORE_COLUMN_LABELS: Record<CoreColumnId, string> = {
  project: 'Projeto',
  issue_type: 'Issue Type',
  go_live_planned: 'Go Live planejado',
  go_live_real: 'Go Live real',
  updated: 'Atualizado',
  created: 'Criado em',
  duedate: 'Data limite',
  hh_vendido_seconds: 'HH Vendido (est.)',
  hh_real_seconds: 'HH Real (gasto)',
  status: 'Fase',
};
const CORE_COLUMN_IDS_SET = new Set<CoreColumnId>(Object.keys(CORE_COLUMN_LABELS) as CoreColumnId[]);

const OBSERVATION_LABEL_HINT = 'observ';
const MAX_COST_SCOPE_CACHE_ITEMS = 160;
const AUTO_SYNC_VIEWS_DEBOUNCE_MS = 1800;

const createViewId = (): string => `projects-view-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const toSavedViewFromGlobal = (view: ProjectGlobalView): ProjectsSavedView => ({
  id: `projects-global-view-${view.id}`,
  backendId: view.id,
  name: view.name,
  context: view.context || '',
  jql: view.jql || '',
  selectedColumns: normalizeSelectedColumns(view.selected_columns || []),
  kpiConfigs: sanitizeKpiConfigs(view.kpi_configs || []),
  columnWidths: sanitizeColumnWidths(view.column_widths || {}),
  isPreset: Boolean(view.is_preset),
  presetSpecId: view.preset_spec_id || undefined,
  carteiraGroup: view.carteira_group === 'sem_receita' || view.carteira_group === 'operacional' || view.carteira_group === 'interno'
    ? view.carteira_group
    : undefined,
  accessLevel: view.access_level === 'np3' ? 'np3' : view.access_level === 'np0' ? 'np0' : undefined,
});

const toGlobalViewPayload = (view: ProjectsSavedView) => ({
  name: view.name.trim(),
  context: view.context || '',
  jql: view.jql || '',
  selected_columns: normalizeSelectedColumns(view.selectedColumns || []),
  kpi_configs: sanitizeKpiConfigs(view.kpiConfigs || []),
  column_widths: sanitizeColumnWidths(view.columnWidths || {}),
  is_preset: Boolean(view.isPreset),
  preset_spec_id: view.presetSpecId || null,
  carteira_group: view.carteiraGroup || null,
  access_level: view.accessLevel || null,
});

const mergeViewsPreferringBackend = (items: ProjectsSavedView[]): ProjectsSavedView[] => {
  const byName = new Map<string, ProjectsSavedView>();
  items.forEach((view) => {
    const key = normalizeMatchText(view.name) || view.id;
    const current = byName.get(key);
    if (!current) {
      byName.set(key, view);
      return;
    }
    if (current.isPreset && !view.isPreset) return;
    if (view.isPreset && !current.isPreset) {
      byName.set(key, view);
      return;
    }
    if (view.backendId && !current.backendId) {
      byName.set(key, view);
    }
  });
  return Array.from(byName.values());
};

const resolvePresetSpecForView = (view: ProjectsSavedView): PmoPresetSpec | undefined => {
  if (view.presetSpecId) {
    const byPresetId = PMO_PRESET_SPECS.find((item) => item.id === view.presetSpecId);
    if (byPresetId) return byPresetId;
  }
  if (view.isPreset) {
    const byViewId = PMO_PRESET_SPECS.find((item) => item.id === view.id);
    if (byViewId) return byViewId;
  }
  const byName = PMO_PRESET_SPECS.find(
    (item) => normalizeMatchText(item.name) === normalizeMatchText(view.name),
  );
  return byName;
};

const buildAccessiblePresetViews = (
  user?: { role?: string; can_access_projects?: boolean; projects_access_level?: string | null } | null,
): ProjectsSavedView[] => {
  const accessLevel = resolveProjectsAccessLevel(user ?? undefined);
  return buildPresetViews()
    .filter((view) => {
      const spec = PMO_PRESET_SPECS.find((item) => item.id === view.presetSpecId);
      return spec ? canViewPreset(spec, accessLevel) : false;
    })
    .map((view) => ({ ...view }));
};

const sanitizeColumnWidths = (value: unknown): Record<string, number> => {
  if (!value || typeof value !== 'object') return {};
  const entries = Object.entries(value as Record<string, unknown>);
  const out: Record<string, number> = {};
  entries.forEach(([columnId, widthValue]) => {
    const widthNumber = Number(widthValue);
    if (Number.isFinite(widthNumber) && widthNumber >= MIN_COLUMN_WIDTH_PX) {
      out[columnId] = Math.round(widthNumber);
    }
  });
  return out;
};

const KPI_OPTIONS_BY_ID: Record<string, ProjectsKpiOption> = KPI_OPTIONS.reduce((acc, item) => {
  acc[item.id] = item;
  return acc;
}, {} as Record<string, ProjectsKpiOption>);

const sanitizeKpiConfigs = (value: unknown): ProjectsKpiConfig[] => {
  const asArray = Array.isArray(value) ? value : [];
  const out: ProjectsKpiConfig[] = [];
  const seen = new Set<string>();
  asArray.forEach((entry) => {
    let id = '';
    let title = '';
    if (typeof entry === 'string') {
      id = entry.trim();
    } else if (entry && typeof entry === 'object') {
      const obj = entry as { id?: unknown; title?: unknown };
      id = typeof obj.id === 'string' ? obj.id.trim() : '';
      title = typeof obj.title === 'string' ? obj.title.trim() : '';
    }
    if (!id || !KPI_OPTIONS_BY_ID[id] || seen.has(id)) return;
    seen.add(id);
    out.push({ id, title: title || undefined });
  });
  if (!out.length) {
    return DEFAULT_KPI_CARDS.map((id) => ({ id }));
  }
  return out;
};

const sanitizeSavedViews = (value: unknown): ProjectsSavedView[] => {
  if (!Array.isArray(value)) return [];
  const defaultColumns = [...CORE_DEFAULT_COLUMNS];
  const defaultKpis = DEFAULT_KPI_CARDS.map((id) => ({ id }));
  return value.reduce<ProjectsSavedView[]>((acc, item) => {
    if (!item || typeof item !== 'object') return acc;
    const candidate = item as Partial<ProjectsSavedView>;
    const id = typeof candidate.id === 'string' && candidate.id.trim() ? candidate.id.trim() : createViewId();
    const name = typeof candidate.name === 'string' ? candidate.name.trim() : '';
    if (!name) return acc;
    acc.push({
      id,
      name,
      context: typeof candidate.context === 'string' ? candidate.context : '',
      jql: typeof candidate.jql === 'string' ? candidate.jql : '',
      selectedColumns: Array.isArray(candidate.selectedColumns)
        ? normalizeSelectedColumns(candidate.selectedColumns.map((value) => String(value)).filter(Boolean))
        : defaultColumns,
      kpiConfigs: Array.isArray((candidate as { kpiConfigs?: unknown }).kpiConfigs)
        ? sanitizeKpiConfigs((candidate as { kpiConfigs?: unknown }).kpiConfigs)
        : Array.isArray((candidate as { kpiCards?: unknown }).kpiCards)
          ? sanitizeKpiConfigs((candidate as { kpiCards?: unknown }).kpiCards)
          : defaultKpis,
      columnWidths: sanitizeColumnWidths((candidate as { columnWidths?: unknown }).columnWidths),
      isPreset: Boolean(candidate.isPreset),
      presetSpecId: typeof candidate.presetSpecId === 'string' ? candidate.presetSpecId : undefined,
      carteiraGroup: candidate.carteiraGroup === 'sem_receita' || candidate.carteiraGroup === 'operacional' || candidate.carteiraGroup === 'interno'
        ? candidate.carteiraGroup
        : undefined,
      accessLevel: candidate.accessLevel === 'np3' ? 'np3' : candidate.accessLevel === 'np0' ? 'np0' : undefined,
    });
    return acc;
  }, []);
};

const storageKeyForUser = (userId?: number): string => `${PROJECTS_VIEWS_STORAGE_KEY}_${userId ?? 'anon'}`;

const renderExtraFieldValue = (value: unknown): string => {
  if (value == null) return '—';
  if (typeof value === 'string') return value.trim() || '—';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const parts = value.map((item) => renderExtraFieldValue(item)).filter((item) => item && item !== '—');
    return parts.length ? parts.join(', ') : '—';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (_err) {
      return '—';
    }
  }
  return String(value);
};

const ProjectsDashboard: React.FC = () => {
  const { user } = useAuth();
  const { setArea } = useArea();
  const location = useLocation();
  const { showConfirm, showAlert } = useModal();
  const canAccess = user?.role === 'admin' || Boolean(user?.can_access_projects);
  const isAdmin = user?.role === 'admin';
  const canManageViews = isAdmin;
  const canEditIssueObservation = canAccess;
  const canAdjustTableLayout = canAccess;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<ProjectsJiraSearchResponse | null>(null);
  const [availableColumns, setAvailableColumns] = useState<ProjectsJiraColumnOption[]>([]);
  const [savedViews, setSavedViews] = useState<ProjectsSavedView[]>([]);
  const [selectedViewId, setSelectedViewId] = useState('');
  const [initializedUserId, setInitializedUserId] = useState<number | null>(null);

  const [viewNameInput, setViewNameInput] = useState('');
  const [contextInput, setContextInput] = useState('');
  const [jqlInput, setJqlInput] = useState('');
  const [columnSearchInput, setColumnSearchInput] = useState('');
  const [selectedColumnsInput, setSelectedColumnsInput] = useState<string[]>(CORE_DEFAULT_COLUMNS);
  const [selectedKpisInput, setSelectedKpisInput] = useState<ProjectsKpiConfig[]>(
    DEFAULT_KPI_CARDS.map((id) => ({ id }))
  );
  const [selectedColumnWidthsInput, setSelectedColumnWidthsInput] = useState<Record<string, number>>({});
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedJql, setAppliedJql] = useState('');
  const [appliedColumns, setAppliedColumns] = useState<string[]>(CORE_DEFAULT_COLUMNS);
  const [appliedKpis, setAppliedKpis] = useState<ProjectsKpiConfig[]>(
    DEFAULT_KPI_CARDS.map((id) => ({ id }))
  );
  const [appliedColumnWidths, setAppliedColumnWidths] = useState<Record<string, number>>({});
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const [draggedKpiId, setDraggedKpiId] = useState<string | null>(null);
  const [resizingColumnId, setResizingColumnId] = useState<string | null>(null);
  const [startAt, setStartAt] = useState(0);
  const [cursorByStartAt, setCursorByStartAt] = useState<Record<number, string | null>>({ 0: null });
  const [hasMoreByStartAt, setHasMoreByStartAt] = useState<Record<number, boolean>>({});
  const cursorByStartAtRef = useRef(cursorByStartAt);
  cursorByStartAtRef.current = cursorByStartAt;
  const [sortColumn, setSortColumn] = useState<SortColumn>('updated');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [pageSize, setPageSize] = useState(50);
  const [issuesForMetrics, setIssuesForMetrics] = useState<JiraProjectIssueRow[] | null>(null);
  const issuesForMetricsRef = useRef(issuesForMetrics);
  issuesForMetricsRef.current = issuesForMetrics;
  const dataRef = useRef(data);
  dataRef.current = data;
  const [loadingMetricsIssues, setLoadingMetricsIssues] = useState(false);
  const [observationDrafts, setObservationDrafts] = useState<Record<string, string>>({});
  const [savingObservationKeys, setSavingObservationKeys] = useState<Record<string, boolean>>({});
  const [readonlyObservationKeys, setReadonlyObservationKeys] = useState<Record<string, string>>({});
  const [costColumnError, setCostColumnError] = useState('');
  const [costAnalysis, setCostAnalysis] = useState<ProjectCostCalculationResponse | null>(null);
  /** Resultado do cálculo da página visível (fase 1) — alimenta colunas sem sobrescrever KPIs full. */
  const [pageCostAnalysis, setPageCostAnalysis] = useState<ProjectCostCalculationResponse | null>(null);
  /** Cálculo isolado para KPIs de HH no período — não alimenta colunas da tabela. */
  const [periodCostAnalysis, setPeriodCostAnalysis] = useState<ProjectCostCalculationResponse | null>(null);
  const [periodCostError, setPeriodCostError] = useState('');
  const [costAnalysisError, setCostAnalysisError] = useState('');
  const [costCalculationPending, setCostCalculationPending] = useState(false);
  const [periodCostCalculationPending, setPeriodCostCalculationPending] = useState(false);
  const [showRevenueCostPanel, setShowRevenueCostPanel] = useState(false);
  const [costAnalysisMaxResults, setCostAnalysisMaxResults] = useState(200);
  const [criticalMarginThresholdPct, setCriticalMarginThresholdPct] = useState(20);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isPmoFiltersCollapsed, setIsPmoFiltersCollapsed] = useState(false);
  const [savePanelTab, setSavePanelTab] = useState<'jql' | 'ai'>('jql');
  const [nlQuestion, setNlQuestion] = useState('');
  const [nlLoading, setNlLoading] = useState(false);
  const [nlError, setNlError] = useState('');
  const [nlResult, setNlResult] = useState<ProjectsNlQueryResponse | null>(null);
  const [showViewSavedIndicator, setShowViewSavedIndicator] = useState(false);
  const [viewsSyncing, setViewsSyncing] = useState(false);
  const [structuredFilters, setStructuredFilters] = useState<PmoStructuredFiltersState>(EMPTY_STRUCTURED_FILTERS);
  const [appliedStructuredFilters, setAppliedStructuredFilters] = useState<PmoStructuredFiltersState>(EMPTY_STRUCTURED_FILTERS);
  const appliedStructuredFiltersRef = useRef(appliedStructuredFilters);
  appliedStructuredFiltersRef.current = appliedStructuredFilters;
  const [activeCarteiraGroup, setActiveCarteiraGroup] = useState<PmoCarteiraGroup>('operacional');
  const resizeRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);
  const latestIssuesRequestKeyRef = useRef('');
  const latestCostScopeKeyRef = useRef('');
  const latestVisibleCostScopeKeyRef = useRef('');
  const latestPeriodCostScopeKeyRef = useRef('');
  const holdTableCostForExportRef = useRef(false);
  const activeVisibleCostRequestsRef = useRef(0);
  const activeFullCostRequestsRef = useRef(0);
  const activePeriodCostRequestsRef = useRef(0);
  const hasLoadedIssuesOnceRef = useRef(false);
  const jiraQueryCacheRef = useRef<Record<string, { data: ProjectsJiraSearchResponse; costAnalysis: ProjectCostCalculationResponse | null }>>({});
  const metricsRowsCacheRef = useRef<Record<string, JiraProjectIssueRow[]>>({});
  const issuesForMetricsScopeKeyRef = useRef('');
  const costScopeCacheRef = useRef<Record<string, ProjectCostCalculationResponse>>({});
  const costScopeCacheOrderRef = useRef<string[]>([]);
  const viewsAutoSyncInFlightRef = useRef(false);
  const viewsAutoSyncTimerRef = useRef<number | null>(null);
  const lastSyncedViewsFingerprintRef = useRef('');
  const topScrollbarRef = useRef<HTMLDivElement | null>(null);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollSyncLockRef = useRef(false);
  const viewsImportInputRef = useRef<HTMLInputElement | null>(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  const [tableClientWidth, setTableClientWidth] = useState(0);
  const costPanelSettingsStorageKey = useMemo(
    () => `projects_dashboard_cost_panel_settings:${user?.email || 'anonymous'}`,
    [user?.email],
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem(costPanelSettingsStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        show?: boolean;
        max_results?: number;
        critical_margin_pct?: number;
      };
      if (typeof parsed.show === 'boolean') setShowRevenueCostPanel(parsed.show);
      if (Number.isFinite(parsed.max_results)) setCostAnalysisMaxResults(Math.max(10, Math.min(5000, Number(parsed.max_results))));
      if (Number.isFinite(parsed.critical_margin_pct)) setCriticalMarginThresholdPct(Math.max(0, Math.min(100, Number(parsed.critical_margin_pct))));
    } catch {
      // ignore invalid local cache
    }
  }, [costPanelSettingsStorageKey]);

  useEffect(() => {
    localStorage.setItem(
      costPanelSettingsStorageKey,
      JSON.stringify({
        show: showRevenueCostPanel,
        max_results: costAnalysisMaxResults,
        critical_margin_pct: criticalMarginThresholdPct,
      }),
    );
  }, [costAnalysisMaxResults, costPanelSettingsStorageKey, criticalMarginThresholdPct, showRevenueCostPanel]);

  useEffect(() => {
    if (!canAccess || !location.pathname.startsWith('/projects')) return;
    setArea('projects');
  }, [canAccess, location.pathname, setArea]);

  const buildSelectedJiraFieldsWithCarteira = useCallback((selectedColumns: string[]): string[] => {
    const selectedJiraFields = selectedColumns.filter(
      (column) => !CORE_COLUMN_IDS_SET.has(column as CoreColumnId) && !COMPUTED_COLUMN_IDS_SET.has(column),
    );
    const requiredCarteiraFields = availableColumns
      .filter((column) => {
        const columnId = String(column.id || '').trim();
        const label = normalizeFieldLabel(String(column.label || ''));
        return (
          label === 'classificacao do atendimento'
          || columnId === 'customfield_11344'
          || columnId === 'customfield_11434'
        );
      })
      .map((column) => String(column.id || '').trim())
      .filter(Boolean);
    return Array.from(new Set([...selectedJiraFields, ...requiredCarteiraFields]));
  }, [availableColumns]);

  const buildJiraQueryCacheKey = useCallback((
    jql: string,
    selectedColumns: string[],
    start: number,
    size: number,
    requiresCostData: boolean,
    costScopeKey: string,
  ): string => {
    const columnsKey = [...selectedColumns].sort().join(',');
    const costKey = requiresCostData ? 'with_cost' : 'without_cost';
    return `${jql.trim()}||${columnsKey}||${start}||${size}||${costKey}||${costScopeKey}`;
  }, []);

  const buildMetricsRowsCacheKey = useCallback((jql: string, selectedColumns: string[]): string => {
    const columnsKey = [...selectedColumns].sort().join(',');
    return `${jql.trim()}||${columnsKey}`;
  }, []);

  const resetCostScopeRuntime = useCallback((clearStoredCache = false) => {
    if (clearStoredCache) {
      costScopeCacheRef.current = {};
      costScopeCacheOrderRef.current = [];
    }
    latestVisibleCostScopeKeyRef.current = '';
    latestCostScopeKeyRef.current = '';
    latestPeriodCostScopeKeyRef.current = '';
  }, []);

  const resetCostAnalysisState = useCallback(() => {
    setPageCostAnalysis(null);
    setCostAnalysis(null);
    setPeriodCostAnalysis(null);
    resetCostScopeRuntime(false);
  }, [resetCostScopeRuntime]);

  const storeCostScopeCache = useCallback((scopeKey: string, result: ProjectCostCalculationResponse) => {
    if (!scopeKey) return;
    costScopeCacheRef.current[scopeKey] = result;
    const currentOrder = costScopeCacheOrderRef.current.filter((key) => key !== scopeKey);
    currentOrder.push(scopeKey);
    while (currentOrder.length > MAX_COST_SCOPE_CACHE_ITEMS) {
      const oldest = currentOrder.shift();
      if (oldest) delete costScopeCacheRef.current[oldest];
    }
    costScopeCacheOrderRef.current = currentOrder;
  }, []);

  const getSearchableColumnValue = useCallback((row: JiraProjectIssueRow, columnId: string): string => {
    if (columnId === 'project') return `${row.summary || ''} ${row.key || ''} ${row.project_key || ''} ${row.project_name || ''}`;
    if (columnId === 'issue_type') return row.issue_type || '';
    if (columnId === 'go_live_planned') return formatDateBR(row.go_live_planned);
    if (columnId === 'go_live_real') return formatDateBR(row.go_live_real);
    if (columnId === 'updated') return formatDateBR(row.updated);
    if (columnId === 'created') return formatDateBR(row.created);
    if (columnId === 'duedate') return formatDateBR(row.duedate);
    if (columnId === 'hh_vendido_seconds') return formatHoursFromSeconds(row.hh_vendido_seconds);
    if (columnId === 'hh_real_seconds') return formatHoursFromSeconds(row.hh_real_seconds);
    if (columnId === 'status') return row.status || '';
    if ([LOCAL_COST_COLUMN_ID, SALE_VALUE_COLUMN_ID, ESTIMATED_COST_COLUMN_ID, OWN_COST_COLUMN_ID, THIRD_COST_COLUMN_ID, ISSUE_COST_COLUMN_ID, COMMERCIAL_MARGIN_COLUMN_ID, SALE_MARGIN_COLUMN_ID].includes(columnId)) {
      return formatCurrencyFromCents(Number(row.extra_fields?.[columnId] || 0));
    }
    if ([OWN_HOURS_COLUMN_ID, THIRD_HOURS_COLUMN_ID].includes(columnId)) {
      const value = Number(row.extra_fields?.[columnId] || 0);
      return Number.isFinite(value)
        ? value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
        : '—';
    }
    if ([COMMERCIAL_MARGIN_PCT_COLUMN_ID, SALE_MARGIN_PCT_COLUMN_ID].includes(columnId)) {
      const value = row.extra_fields?.[columnId];
      return value == null ? '—' : `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
    }
    return renderExtraFieldValue(row.extra_fields?.[columnId]);
  }, []);

  const appliedKpisRef = useRef(appliedKpis);
  appliedKpisRef.current = appliedKpis;

  const fetchIssues = useCallback(async (params: {
    jql: string;
    selectedColumns: string[];
    startAt: number;
    pageSize: number;
    cursor?: string | null;
  }) => {
    const nextJql = params.jql.trim();
    const nextColumns = params.selectedColumns;
    const nextStartAt = Math.max(0, Number(params.startAt || 0));
    const nextPageSize = Math.max(1, Math.min(100, Number(params.pageSize || 50)));
    const nextCursor = String(params.cursor || '').trim() || null;
    const requiresCostData = nextColumns.some((column) => COMPUTED_COLUMN_IDS_SET.has(column))
      || hasExecutiveKpi(extractKpiIds(appliedKpisRef.current));
    const structuredFiltersActiveForScope = hasActivePmoRowFilters(appliedStructuredFiltersRef.current);
    const costScopeKey = requiresCostData ? 'page_cost' : 'no_cost_scope';
    const cacheKey = buildJiraQueryCacheKey(nextJql, nextColumns, nextStartAt, nextPageSize, requiresCostData, costScopeKey);
    const cached = jiraQueryCacheRef.current[cacheKey];
    if (cached) {
      // Já aplicamos este resultado: não chamar setState de novo (loop + apaga decorate de custo).
      if (latestIssuesRequestKeyRef.current === cacheKey) {
        return;
      }
      latestIssuesRequestKeyRef.current = cacheKey;
      setData(cached.data);
      setCostAnalysis(cached.costAnalysis);
      const cachedHasMore = Boolean(cached.data.has_more);
      setHasMoreByStartAt((prev) => (
        prev[nextStartAt] === cachedHasMore ? prev : { ...prev, [nextStartAt]: cachedHasMore }
      ));
      if (cached.data.next_cursor) {
        const nextPageCursor = cached.data.next_cursor || null;
        setCursorByStartAt((prev) => (
          prev[nextStartAt] === nextCursor && prev[nextStartAt + nextPageSize] === nextPageCursor
            ? prev
            : {
              ...prev,
              [nextStartAt]: nextCursor,
              [nextStartAt + nextPageSize]: nextPageCursor,
            }
        ));
      }
      return;
    }
    const selectedJiraFieldsWithCarteira = buildSelectedJiraFieldsWithCarteira(nextColumns);
    const shouldShowBlockingLoading = !hasLoadedIssuesOnceRef.current;
    if (shouldShowBlockingLoading) setLoading(true);
    setError('');
    setCostColumnError('');
    setCostAnalysisError('');
    const requestToken = `${cacheKey}::${Date.now()}::${Math.random()}`;
    latestIssuesRequestKeyRef.current = requestToken;
    try {
      const res = await axios.get<ProjectsJiraSearchResponse>(`${API_URL}/api/projects/issues`, {
        params: {
          jql: nextJql,
          selected_fields: selectedJiraFieldsWithCarteira,
          start_at: nextStartAt,
          max_results: nextPageSize,
          cursor: nextCursor || undefined,
        },
      });
      if (latestIssuesRequestKeyRef.current !== requestToken) return;
      const baseIssues = res.data.issues || [];
      if (!requiresCostData) {
        const nextData: ProjectsJiraSearchResponse = { ...res.data, issues: baseIssues };
        setData(nextData);
        hasLoadedIssuesOnceRef.current = true;
        latestIssuesRequestKeyRef.current = cacheKey;
        setCostAnalysis(null);
        setPageCostAnalysis(null);
        setPeriodCostAnalysis(null);
        const nextHasMore = Boolean(nextData.has_more);
        setHasMoreByStartAt((prev) => (
          prev[nextStartAt] === nextHasMore ? prev : { ...prev, [nextStartAt]: nextHasMore }
        ));
        if (nextData.next_cursor) {
          const nextPageCursor = nextData.next_cursor || null;
          setCursorByStartAt((prev) => (
            prev[nextStartAt] === nextCursor && prev[nextStartAt + nextPageSize] === nextPageCursor
              ? prev
              : {
                ...prev,
                [nextStartAt]: nextCursor,
                [nextStartAt + nextPageSize]: nextPageCursor,
              }
          ));
        } else {
          setCursorByStartAt((prev) => (
            prev[nextStartAt] === nextCursor ? prev : { ...prev, [nextStartAt]: nextCursor }
          ));
        }
        jiraQueryCacheRef.current[cacheKey] = {
          data: nextData,
          costAnalysis: null,
        };
        return;
      }

      const baseData: ProjectsJiraSearchResponse = { ...res.data, issues: baseIssues };
      setData(baseData);
      hasLoadedIssuesOnceRef.current = true;
      latestIssuesRequestKeyRef.current = cacheKey;
      const nextHasMore = Boolean(baseData.has_more);
      setHasMoreByStartAt((prev) => (
        prev[nextStartAt] === nextHasMore ? prev : { ...prev, [nextStartAt]: nextHasMore }
      ));
      if (baseData.next_cursor) {
        const nextPageCursor = baseData.next_cursor || null;
        setCursorByStartAt((prev) => (
          prev[nextStartAt] === nextCursor && prev[nextStartAt + nextPageSize] === nextPageCursor
            ? prev
            : {
              ...prev,
              [nextStartAt]: nextCursor,
              [nextStartAt + nextPageSize]: nextPageCursor,
            }
        ));
      } else {
        setCursorByStartAt((prev) => (
          prev[nextStartAt] === nextCursor ? prev : { ...prev, [nextStartAt]: nextCursor }
        ));
      }
      jiraQueryCacheRef.current[cacheKey] = {
        data: baseData,
        costAnalysis: null,
      };
      if (structuredFiltersActiveForScope) {
        latestCostScopeKeyRef.current = '';
      }
      return;
    } catch (e: unknown) {
      const ax = e as { response?: { status?: number; data?: { detail?: string } } };
      if (ax.response?.status === 422) {
        setError('Informe uma JQL válida para buscar dados.');
      } else {
        setError(ax.response?.data?.detail || 'Não foi possível carregar os dados do Jira.');
      }
      setData(null);
    } finally {
      if (shouldShowBlockingLoading) setLoading(false);
    }
  }, [
    buildJiraQueryCacheKey,
    buildSelectedJiraFieldsWithCarteira,
  ]);

  const fetchIssuesRef = useRef(fetchIssues);
  fetchIssuesRef.current = fetchIssues;

  const fetchAllIssuesForMetrics = useCallback(async (params: { jql: string; selectedColumns: string[] }) => {
    const nextJql = params.jql.trim();
    if (!nextJql) {
      setIssuesForMetrics([]);
      issuesForMetricsScopeKeyRef.current = '';
      return;
    }
    const cacheKey = buildMetricsRowsCacheKey(nextJql, params.selectedColumns);
    const cached = metricsRowsCacheRef.current[cacheKey];
    if (cached) {
      if (issuesForMetricsScopeKeyRef.current === cacheKey) {
        return;
      }
      issuesForMetricsScopeKeyRef.current = cacheKey;
      setIssuesForMetrics(cached);
      return;
    }

    const selectedJiraFieldsWithCarteira = buildSelectedJiraFieldsWithCarteira(params.selectedColumns);
    setLoadingMetricsIssues(true);
    try {
      const allIssues: JiraProjectIssueRow[] = [];
      let start = 0;
      let cursor: string | null = null;
      const batchSize = 100;

      while (true) {
        const res: { data: ProjectsJiraSearchResponse } = await axios.get<ProjectsJiraSearchResponse>(`${API_URL}/api/projects/issues`, {
          params: {
            jql: nextJql,
            selected_fields: selectedJiraFieldsWithCarteira,
            start_at: start,
            max_results: batchSize,
            cursor: cursor || undefined,
          },
        });
        const pageIssues = res.data.issues || [];
        allIssues.push(...pageIssues);
        const hasMore = Boolean(res.data.has_more);
        cursor = res.data.next_cursor || null;
        if (!hasMore || !pageIssues.length) break;
        start += pageIssues.length;
      }

      metricsRowsCacheRef.current[cacheKey] = allIssues;
      issuesForMetricsScopeKeyRef.current = cacheKey;
      setIssuesForMetrics(allIssues);
    } catch {
      issuesForMetricsScopeKeyRef.current = '';
      setIssuesForMetrics(null);
    } finally {
      setLoadingMetricsIssues(false);
    }
  }, [buildMetricsRowsCacheKey, buildSelectedJiraFieldsWithCarteira]);

  const fetchColumns = useCallback(async () => {
    try {
      const response = await axios.get<ProjectsJiraColumnOption[]>(`${API_URL}/api/projects/columns`);
      setAvailableColumns(response.data || []);
    } catch (_err) {
      setAvailableColumns([]);
    }
  }, []);

  const loadGlobalViewsFromApi = useCallback(async (): Promise<ProjectsSavedView[]> => {
    const response = await axios.get<ProjectGlobalView[]>(`${API_URL}/api/projects/views`);
    const items = Array.isArray(response.data) ? response.data : [];
    return items.map((item) => toSavedViewFromGlobal(item));
  }, []);

  const createGlobalView = useCallback(async (view: ProjectsSavedView): Promise<ProjectsSavedView> => {
    const response = await axios.post<{ id: number }>(
      `${API_URL}/api/projects/views`,
      toGlobalViewPayload(view),
    );
    const createdId = Number(response.data?.id);
    const reloaded = await loadGlobalViewsFromApi();
    const persisted = reloaded.find((item) => item.backendId === createdId);
    return persisted ?? { ...view, backendId: Number.isFinite(createdId) ? createdId : undefined };
  }, [loadGlobalViewsFromApi]);

  const updateGlobalView = useCallback(async (view: ProjectsSavedView): Promise<ProjectsSavedView> => {
    if (!view.backendId) {
      return createGlobalView(view);
    }
    await axios.put(
      `${API_URL}/api/projects/views/${view.backendId}`,
      toGlobalViewPayload(view),
    );
    const reloaded = await loadGlobalViewsFromApi();
    return reloaded.find((item) => item.backendId === view.backendId) ?? view;
  }, [createGlobalView, loadGlobalViewsFromApi]);

  const upsertGlobalViewByName = useCallback(async (view: ProjectsSavedView): Promise<ProjectsSavedView> => {
    const payload: ProjectGlobalViewsImportPayload = {
      schema_version: 1,
      views: [toGlobalViewPayload(view)],
    };
    await axios.post<ProjectGlobalViewsImportResponse>(
      `${API_URL}/api/projects/views/import`,
      payload,
    );
    const reloaded = await loadGlobalViewsFromApi();
    const normalizedName = normalizeMatchText(view.name);
    const matched = reloaded.find((item) => normalizeMatchText(item.name) === normalizedName);
    if (matched) return matched;
    return updateGlobalView(view);
  }, [loadGlobalViewsFromApi, updateGlobalView]);

  const deleteGlobalView = useCallback(async (view: ProjectsSavedView): Promise<void> => {
    if (!view.backendId) return;
    await axios.delete(`${API_URL}/api/projects/views/${view.backendId}`);
  }, []);

  const exportGlobalViews = useCallback(async (): Promise<void> => {
    setViewsSyncing(true);
    try {
      const viewsToSync = savedViews.filter((view) => Boolean(view.name.trim()));
      if (viewsToSync.length > 0) {
        const syncPayload: ProjectGlobalViewsImportPayload = {
          schema_version: 1,
          views: viewsToSync.map((view) => ({
            name: view.name.trim(),
            context: view.context || '',
            jql: view.jql || '',
            selected_columns: normalizeSelectedColumns(view.selectedColumns || []),
            kpi_configs: sanitizeKpiConfigs(view.kpiConfigs || []),
            column_widths: sanitizeColumnWidths(view.columnWidths || {}),
            is_preset: Boolean(view.isPreset),
            preset_spec_id: view.presetSpecId || null,
            carteira_group: view.carteiraGroup || null,
            access_level: view.accessLevel || null,
          })),
        };
        await axios.post<ProjectGlobalViewsImportResponse>(
          `${API_URL}/api/projects/views/import`,
          syncPayload,
        );
      }
      const response = await axios.get<ProjectGlobalViewsExportPayload>(`${API_URL}/api/projects/views/export`);
      const fileName = `projects-views-export-${new Date().toISOString().slice(0, 10)}.json`;
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const totalViews = Array.isArray(response.data?.views) ? response.data.views.length : 0;
      showAlert({
        title: 'Exportação concluída',
        message: `Arquivo de visões exportado com sucesso (${totalViews} visão(ões)).`,
        variant: 'success',
      });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      showAlert({
        title: 'Erro ao exportar visões',
        message: ax.response?.data?.detail || 'Não foi possível exportar as visões globais.',
        variant: 'danger',
      });
    } finally {
      setViewsSyncing(false);
    }
  }, [savedViews, showAlert]);

  const importGlobalViewsFromFile = useCallback(async (file: File): Promise<void> => {
    setViewsSyncing(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as ProjectGlobalViewsImportPayload;
      const payload: ProjectGlobalViewsImportPayload = {
        schema_version: Number(parsed?.schema_version || 1),
        views: Array.isArray(parsed?.views) ? parsed.views : [],
      };
      const response = await axios.post<ProjectGlobalViewsImportResponse>(
        `${API_URL}/api/projects/views/import`,
        payload,
      );
      const result = response.data;
      const reloaded = await loadGlobalViewsFromApi();
      setSavedViews((prev) => {
        const presets = prev.filter((item) => item.isPreset && !item.backendId);
        return mergeViewsPreferringBackend([...presets, ...reloaded]);
      });
      showAlert({
        title: 'Importação concluída',
        message: `Criadas ${result.created}, atualizadas ${result.updated}, ignoradas ${result.skipped}.`,
        variant: 'success',
      });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      showAlert({
        title: 'Erro ao importar visões',
        message: ax.response?.data?.detail || 'Arquivo inválido ou erro ao importar visões.',
        variant: 'danger',
      });
    } finally {
      setViewsSyncing(false);
      if (viewsImportInputRef.current) viewsImportInputRef.current.value = '';
    }
  }, [loadGlobalViewsFromApi, showAlert]);

  const applyView = useCallback((view: ProjectsSavedView) => {
    setSelectedViewId(view.id);
    setViewNameInput(view.name);
    setContextInput(view.context || '');
    const presetSpec = resolvePresetSpecForView(view);
    // Colunas de preset vêm da spec atual. KPIs respeitam o que o usuário habilitou/renomeou na visão.
    const nextCols = normalizeSelectedColumns(
      presetSpec?.columnIds?.length
        ? presetSpec.columnIds
        : ((view.selectedColumns && view.selectedColumns.length > 0) ? view.selectedColumns : CORE_DEFAULT_COLUMNS),
    );
    // Exceção: o comparativo Compras-e tem que espelhar a tela Compras-e, então os cards vêm sempre da spec.
    const nextKpis = presetSpec?.id === COMPRAS_E_COMPARATIVO_PRESET_ID
      ? reconcileComprasEPresetKpis(presetSpec, view.kpiConfigs || [])
      : ((view.kpiConfigs && view.kpiConfigs.length > 0)
        ? view.kpiConfigs
        : (presetSpec?.kpiIds?.length
          ? presetSpec.kpiIds.map((id) => ({ id }))
          : DEFAULT_KPI_CARDS.map((id) => ({ id }))));
    const nextWidths = view.columnWidths || {};
    setSelectedColumnsInput(nextCols);
    setSelectedKpisInput(nextKpis);
    setSelectedColumnWidthsInput(nextWidths);
    setAppliedColumns(nextCols);
    setAppliedKpis(nextKpis);
    setAppliedColumnWidths(nextWidths);
    setSearchInput('');
    if (presetSpec?.id === COMPRAS_E_COMPARATIVO_PRESET_ID) {
      const nextFilters = pmoComparativoDefaultFilters();
      setStructuredFilters(nextFilters);
      try {
        validatePmoRowFilters(toEffectivePmoFilters(nextFilters));
        setAppliedStructuredFilters(toEffectivePmoFilters(nextFilters));
      } catch {
        setAppliedStructuredFilters(EMPTY_STRUCTURED_FILTERS);
      }
    }
    setIssuesForMetrics(null);
    resetCostAnalysisState();
    setIsPmoFiltersCollapsed(false);
    issuesForMetricsScopeKeyRef.current = '';
    latestIssuesRequestKeyRef.current = '';

    if (presetSpec) {
      setActiveCarteiraGroup(view.carteiraGroup || presetSpec.carteiraGroup || 'operacional');
      // Sempre prioriza a JQL da própria visão (permite persistir ajustes em presets).
      // Exceção: no comparativo Compras-e a JQL vem sempre da spec — visões já salvas
      // ficariam presas ao pool antigo (só "História") e perderiam os pais Epic/Bug/Melhoria.
      const nextViewJql = presetSpec.id === COMPRAS_E_COMPARATIVO_PRESET_ID
        ? (presetSpec.baseJql || '')
        : (view.jql || presetSpec.baseJql || '');
      setJqlInput(nextViewJql);
      setAppliedJql(nextViewJql);
    } else {
      setActiveCarteiraGroup('operacional');
      setJqlInput(view.jql || '');
      setAppliedJql(view.jql || '');
    }
    setStartAt(0);
    setCursorByStartAt({ 0: null });
    setHasMoreByStartAt({});
  }, []);

  useEffect(() => {
    if (!canAccess || !user) return;
    if (initializedUserId === user.id) return;
    let cancelled = false;
    const initialize = async () => {
      const presets = buildAccessiblePresetViews(user);
      const key = storageKeyForUser(user.id);
      let persistedViews: ProjectsSavedView[] = [];
      let selectedPersistedId = '';
      let persistedFilters: PmoStructuredFiltersState | null = null;
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsedRaw = JSON.parse(raw) as unknown;
          selectedPersistedId = (
            parsedRaw &&
            typeof parsedRaw === 'object' &&
            typeof (parsedRaw as { selectedViewId?: unknown }).selectedViewId === 'string'
          )
            ? String((parsedRaw as { selectedViewId?: string }).selectedViewId)
            : '';
          if (parsedRaw && typeof parsedRaw === 'object' && 'structuredFilters' in parsedRaw) {
            persistedFilters = sanitizeStructuredFilters(
              (parsedRaw as { structuredFilters?: unknown }).structuredFilters,
            );
          }
          const candidateViews = (
            parsedRaw && typeof parsedRaw === 'object' && Array.isArray((parsedRaw as { views?: unknown }).views)
              ? (parsedRaw as { views: unknown[] }).views
              : parsedRaw
          );
          persistedViews = sanitizeSavedViews(candidateViews);
        }
      } catch {
        persistedViews = [];
        persistedFilters = null;
      }

      let serverViews: ProjectsSavedView[] = [];
      try {
        serverViews = await loadGlobalViewsFromApi();
      } catch {
        serverViews = [];
      }

      const serverNames = new Set(serverViews.map((item) => normalizeMatchText(item.name)));
      const legacyOnlyViews = canManageViews
        ? persistedViews.filter((item) => !serverNames.has(normalizeMatchText(item.name)))
        : [];

      let loadedViews = mergeViewsPreferringBackend([...presets, ...serverViews, ...legacyOnlyViews]);

      const continuityBase = loadedViews.find((view) => normalizeMatchText(view.name) === normalizeMatchText(CONTINUITY_VIEW_NAME_BASE));
      const hasContinuityFinance = loadedViews.some((view) => normalizeMatchText(view.name) === normalizeMatchText(CONTINUITY_FINANCE_VIEW_NAME));
      if (continuityBase && !hasContinuityFinance) {
        const financeColumns = normalizeSelectedColumns([
          'project',
          'hh_vendido_seconds',
          'hh_real_seconds',
          ESTIMATED_COST_COLUMN_ID,
          OWN_COST_COLUMN_ID,
          THIRD_COST_COLUMN_ID,
          ISSUE_COST_COLUMN_ID,
          'go_live_real',
          SALE_VALUE_COLUMN_ID,
          'status',
        ]);
        const financeKpis: ProjectsKpiConfig[] = [
          { id: 'total_items', title: 'Projetos ativos' },
          { id: 'hh_vendido_total', title: 'HH previsto' },
          { id: 'hh_real_total', title: 'HH realizado' },
          { id: 'custo_previsto_total', title: 'Custo previsto' },
          { id: 'custo_total', title: 'Custo realizado' },
        ];
        loadedViews = [
          ...loadedViews,
          {
            id: createViewId(),
            name: CONTINUITY_FINANCE_VIEW_NAME,
            context: 'Visão executiva financeira',
            jql: continuityBase.jql || 'project = PORTAL AND resolution is empty ORDER BY updated DESC, created DESC',
            selectedColumns: financeColumns,
            kpiConfigs: financeKpis,
            columnWidths: {},
            isPreset: false,
          },
        ];
      }

      if (cancelled) return;
      setSavedViews(loadedViews);
      const preferredView = loadedViews.find((item) => item.id === selectedPersistedId)
        || loadedViews.find((item) => item.isPreset)
        || loadedViews[0];
      if (preferredView) {
        applyView(preferredView);
        if (persistedFilters && hasActivePmoRowFilters(persistedFilters)) {
          setStructuredFilters(persistedFilters);
          try {
            validatePmoRowFilters(persistedFilters);
            setAppliedStructuredFilters(persistedFilters);
          } catch {
            /* mantém o que applyView definiu */
          }
        }
      } else {
        const defaultKpis = DEFAULT_KPI_CARDS.map((id) => ({ id }));
        setSelectedViewId('');
        setViewNameInput('');
        setContextInput('');
        setJqlInput('');
        setSelectedColumnsInput(CORE_DEFAULT_COLUMNS);
        setSelectedKpisInput(defaultKpis);
        setSelectedColumnWidthsInput({});
        setAppliedColumns(CORE_DEFAULT_COLUMNS);
        setAppliedKpis(defaultKpis);
        setAppliedColumnWidths({});
        setSearchInput('');
        setAppliedSearch('');
        setAppliedJql('');
        setStartAt(0);
        setCursorByStartAt({ 0: null });
        setHasMoreByStartAt({});
        setData(null);
        setCostAnalysis(null);
      }
      setInitializedUserId(user.id);
    };
    void initialize();
    return () => {
      cancelled = true;
    };
  }, [applyView, canAccess, canManageViews, initializedUserId, loadGlobalViewsFromApi, user]);

  useEffect(() => {
    if (!canAccess) return;
    void fetchColumns();
  }, [canAccess, fetchColumns]);

  useEffect(() => {
    if (!user) return;
    if (!savedViews.length && !selectedViewId) return;
    localStorage.setItem(
      storageKeyForUser(user.id),
      JSON.stringify({
        schemaVersion: PROJECTS_VIEWS_SCHEMA_VERSION,
        views: savedViews,
        selectedViewId: selectedViewId || savedViews[0]?.id || '',
        structuredFilters,
      })
    );
  }, [savedViews, selectedViewId, structuredFilters, user]);

  // Auto-sync de visões para o backend desativado:
  // empurrava localStorage antigo e sobrescrevia JQLs corrigidas no servidor.
  // Persistência continua no localStorage; sync explícito via Salvar/Importar.

  // Pré-visualização imediata dos cards KPI:
  // ao marcar/desmarcar, renomear ou reordenar, refletir no topo sem exigir "Aplicar query".
  useEffect(() => {
    setAppliedKpis((prev) => (
      JSON.stringify(prev) === JSON.stringify(selectedKpisInput) ? prev : selectedKpisInput
    ));
  }, [selectedKpisInput]);

  const selectedView = useMemo(
    () => savedViews.find((item) => item.id === selectedViewId) || null,
    [savedViews, selectedViewId]
  );

  const isPresetViewActive = Boolean(selectedView?.isPreset);
  const showCarteiraBreakdown = !appliedStructuredFilters.carteiraId.trim();

  const filterWidthsByColumns = useCallback((columns: string[], widths: Record<string, number>): Record<string, number> => {
    const out: Record<string, number> = {};
    columns.forEach((columnId) => {
      const width = widths[columnId];
      if (typeof width === 'number' && width >= MIN_COLUMN_WIDTH_PX) {
        out[columnId] = width;
      }
    });
    return out;
  }, []);

  // Persistência automática dos KPIs da tela atual.
  // Evita perda de título/ordem ao atualizar a página.
  useEffect(() => {
    if (!selectedViewId) return;
    setSavedViews((prev) => {
      const idx = prev.findIndex((item) => item.id === selectedViewId);
      if (idx < 0) return prev;
      const current = prev[idx];
      const currentConfigs = current.kpiConfigs?.length ? current.kpiConfigs : DEFAULT_KPI_CARDS.map((id) => ({ id }));
      if (JSON.stringify(currentConfigs) === JSON.stringify(selectedKpisInput)) {
        return prev;
      }
      const next = [...prev];
      next[idx] = { ...current, kpiConfigs: selectedKpisInput };
      return next;
    });
  }, [selectedKpisInput, selectedViewId]);

  // Persistência automática da tela atual (nome, contexto, JQL, colunas e larguras).
  // Assim, qualquer ajuste feito pelo usuário fica salvo imediatamente.
  useEffect(() => {
    if (!selectedViewId) return;
    setSavedViews((prev) => {
      const idx = prev.findIndex((item) => item.id === selectedViewId);
      if (idx < 0) return prev;
      const current = prev[idx];
      const normalizedColumns = normalizeSelectedColumns(selectedColumnsInput);
      const nextView: ProjectsSavedView = {
        ...current,
        name: viewNameInput || current.name,
        context: contextInput,
        jql: jqlInput,
        selectedColumns: normalizedColumns.length ? normalizedColumns : CORE_DEFAULT_COLUMNS,
        kpiConfigs: selectedKpisInput.length ? selectedKpisInput : DEFAULT_KPI_CARDS.map((id) => ({ id })),
        columnWidths: filterWidthsByColumns(normalizedColumns, selectedColumnWidthsInput),
      };
      if (JSON.stringify(current) === JSON.stringify(nextView)) {
        return prev;
      }
      const next = [...prev];
      next[idx] = nextView;
      return next;
    });
  }, [
    contextInput,
    filterWidthsByColumns,
    jqlInput,
    selectedColumnWidthsInput,
    selectedColumnsInput,
    selectedKpisInput,
    selectedViewId,
    viewNameInput,
  ]);

  const applyFilters = () => {
    const nextSearch = searchInput.trim();
    const nextJql = jqlInput.trim();
    const normalizedColumns = normalizeSelectedColumns(selectedColumnsInput);
    if (!nextJql) {
      setError('A JQL da visão é obrigatória. Preencha para buscar dados.');
      return;
    }
    if (!normalizedColumns.length) {
      setError('Selecione ao menos uma coluna para a visão.');
      return;
    }
    const filteredWidths = filterWidthsByColumns(normalizedColumns, selectedColumnWidthsInput);
    const queryChanged = nextJql !== appliedJql
      || JSON.stringify(normalizedColumns) !== JSON.stringify(appliedColumns);
    setSelectedColumnsInput(normalizedColumns);
    setAppliedSearch(nextSearch);
    setAppliedJql(nextJql);
    setAppliedColumns(normalizedColumns);
    setAppliedKpis(selectedKpisInput);
    setAppliedColumnWidths(filteredWidths);
    if (queryChanged) {
      jiraQueryCacheRef.current = {};
      metricsRowsCacheRef.current = {};
      issuesForMetricsScopeKeyRef.current = '';
      resetCostScopeRuntime(false);
      setIssuesForMetrics(null);
      setCostAnalysis(null);
      setCursorByStartAt({ 0: null });
      setHasMoreByStartAt({});
    }
    if (startAt !== 0) {
      setStartAt(0);
      setCursorByStartAt({ 0: null });
      setHasMoreByStartAt({});
      return;
    }
    setError('');
  };

  const applyStructuredFiltersDraft = useCallback((draft: PmoStructuredFiltersState) => {
    const partialPeriod = hasPartialPeriodInput(draft);
    const effective = toEffectivePmoFilters(draft);
    // Força recálculo de custos/KPIs para o novo escopo filtrado (carteira, período, etc.).
    resetCostAnalysisState();
    setStartAt(0);

    if (!hasActivePmoRowFilters(effective)) {
      setAppliedStructuredFilters(EMPTY_STRUCTURED_FILTERS);
      setError(partialPeriod ? 'Complete o período em dd/mm/aaaa ou mm/aaaa, ou limpe o campo.' : '');
      return;
    }
    try {
      validatePmoRowFilters(effective);
      setAppliedStructuredFilters(effective);
      setError(
        partialPeriod
          ? 'Período incompleto ignorado; demais filtros aplicados na tabela.'
          : '',
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Filtros PMO inválidos.');
    }
  }, [resetCostAnalysisState]);

  const applyStructuredFilters = useCallback(() => {
    applyStructuredFiltersDraft(structuredFilters);
  }, [applyStructuredFiltersDraft, structuredFilters]);

  const handleStructuredFiltersChange = useCallback((next: PmoStructuredFiltersState) => {
    setStructuredFilters(next);
    applyStructuredFiltersDraft(next);
  }, [applyStructuredFiltersDraft]);

  const pmoFiltersActive = hasActivePmoRowFilters(appliedStructuredFilters);
  const requiresCostColumns = useMemo(
    () => appliedColumns.some((column) => COMPUTED_COLUMN_IDS_SET.has(column)),
    [appliedColumns],
  );
  const pmoWorklogPeriod = useMemo(
    () => pmoPeriodToWorklogIsoDates(
      appliedStructuredFilters.periodStart,
      appliedStructuredFilters.periodEnd,
    ),
    [appliedStructuredFilters.periodEnd, appliedStructuredFilters.periodStart],
  );
  const pmoWorklogPeriodActive = useMemo(
    () => hasCompletePmoWorklogPeriod(appliedStructuredFilters.periodStart, appliedStructuredFilters.periodEnd),
    [appliedStructuredFilters.periodEnd, appliedStructuredFilters.periodStart],
  );
  const appliedPeriodReportScopeApi = useMemo(
    () => toPeriodReportScopeApi(appliedStructuredFilters.reportScope),
    [appliedStructuredFilters.reportScope],
  );

  const displayKpiConfigs = appliedKpis;

  const effectiveKpiIds = useMemo(
    () => extractKpiIds(displayKpiConfigs),
    [displayKpiConfigs],
  );

  /** Comparativo Compras-e com metodologia do relatório — recorta linhas por apontamento. */
  const isComprasEWorklogView = useMemo(() => {
    const view = savedViews.find((item) => item.id === selectedViewId);
    const presetSpecId = view ? resolvePresetSpecForView(view)?.id : undefined;
    return presetSpecId === COMPRAS_E_COMPARATIVO_PRESET_ID
      && isExportWorklogPeriodScope(appliedStructuredFilters.reportScope);
  }, [appliedStructuredFilters.reportScope, savedViews, selectedViewId]);

  // Nessa visão o recálculo do relatório também define quais linhas entram, então roda
  // mesmo que os cards de período estejam desligados.
  const requiresWorklogPeriodCost = useMemo(
    () => pmoWorklogPeriodActive && (effectiveKpiIds.some(isPeriodWorklogKpiId) || isComprasEWorklogView),
    [effectiveKpiIds, isComprasEWorklogView, pmoWorklogPeriodActive],
  );
  /** Colunas de custo ou KPIs financeiros/executivos — não inclui KPIs de período. */
  const requiresExecutiveCostData = useMemo(
    () => requiresCostColumns || hasExecutiveKpi(effectiveKpiIds),
    [effectiveKpiIds, requiresCostColumns],
  );
  const requiresCostData = useMemo(
    () => requiresExecutiveCostData || requiresWorklogPeriodCost,
    [requiresExecutiveCostData, requiresWorklogPeriodCost],
  );
  const exportPeriodNotSettled = shouldHoldExecutiveCostUntilExportSettles({
    reportScopeApi: appliedPeriodReportScopeApi,
    requiresWorklogPeriodCost,
    periodCostCalculationPending,
    hasPeriodCostAnalysis: periodCostAnalysis != null,
    periodCostError,
  });
  holdTableCostForExportRef.current = exportPeriodNotSettled;

  useEffect(() => {
    if (!exportPeriodNotSettled) return;
    // Aborta Fase 1/3a já disparada (senão compete com o Jira por muitos minutos).
    latestVisibleCostScopeKeyRef.current = '';
    latestCostScopeKeyRef.current = '';
  }, [exportPeriodNotSettled]);

  useEffect(() => {
    if (pmoWorklogPeriodActive) return;
    setPeriodCostAnalysis(null);
    setPeriodCostError('');
    latestPeriodCostScopeKeyRef.current = '';
  }, [pmoWorklogPeriodActive]);

  useEffect(() => {
    if (appliedPeriodReportScopeApi !== 'planilha_export_worklogs') return;
    setPeriodCostAnalysis(null);
    setPeriodCostError('');
    latestPeriodCostScopeKeyRef.current = '';
  }, [
    appliedPeriodReportScopeApi,
    pmoWorklogPeriod.worklogEndDate,
    pmoWorklogPeriod.worklogStartDate,
  ]);

  const useMetricsRowsForTable = pmoFiltersActive && Array.isArray(issuesForMetrics);

  const carteiraFieldIdsForFilters = useMemo(() => {
    const preferred = availableColumns
      .filter((column) => {
        const label = normalizeFieldLabel(String(column.label || ''));
        return label.includes('classificacao') && label.includes('atendimento');
      })
      .map((column) => String(column.id || '').trim())
      .filter(Boolean);
    const known = availableColumns
      .filter((column) => column.id === 'customfield_11344' || column.id === 'customfield_11434')
      .map((column) => String(column.id));
    const unique = [...preferred, ...known].filter((item, index, arr) => arr.indexOf(item) === index);
    return unique.length ? unique : [PMO_CARTEIRA_FIELD_DEFAULT];
  }, [availableColumns]);
  const ignoredStatusVisibleColumnIds = useMemo(() => {
    return availableColumns
      .filter((column) => normalizeFieldLabel(String(column.label || '')).includes('observ'))
      .map((column) => String(column.id || '').trim())
      .filter(Boolean);
  }, [availableColumns]);

  /**
   * Só o comparativo Compras-e restringe as linhas às Histórias com apontamento no De/Até.
   * Enquanto o recálculo do relatório não volta, fica nulo e nada é descartado.
   */
  const comprasEWorklogParentKeys = useMemo(() => (
    isComprasEWorklogView
      ? (periodCostAnalysis?.export_scope_worklog_parent_keys ?? null)
      : null
  ), [isComprasEWorklogView, periodCostAnalysis]);

  /**
   * Nesse recorte o De/Até já é aplicado via worklog; manter o filtro por data da
   * História (criação/go live) derrubaria Histórias que apontaram horas no período.
   */
  const rowFiltersForScope = useMemo(
    () => (comprasEWorklogParentKeys
      ? { ...appliedStructuredFilters, periodStart: '', periodEnd: '' }
      : appliedStructuredFilters),
    [appliedStructuredFilters, comprasEWorklogParentKeys],
  );

  const pmoFilteredIssues = useMemo(
    () => filterRowsWithWorklogInPeriod(
      filterPmoRows(
        (useMetricsRowsForTable ? issuesForMetrics : data?.issues) || [],
        rowFiltersForScope,
        activeCarteiraGroup,
        {
          carteiraFieldId: carteiraFieldIdsForFilters[0],
          carteiraFieldIds: carteiraFieldIdsForFilters,
          visibleColumns: appliedColumns,
          ignoredVisibleColumns: ignoredStatusVisibleColumnIds,
        },
      ),
      comprasEWorklogParentKeys,
    ),
    [activeCarteiraGroup, appliedColumns, comprasEWorklogParentKeys, rowFiltersForScope, carteiraFieldIdsForFilters, ignoredStatusVisibleColumnIds, useMetricsRowsForTable, issuesForMetrics, data?.issues],
  );

  const pmoFilteredIssuesForMetrics = useMemo(
    () => filterRowsWithWorklogInPeriod(
      filterPmoRows(
        (issuesForMetrics ?? data?.issues) || [],
        rowFiltersForScope,
        activeCarteiraGroup,
        {
          carteiraFieldId: carteiraFieldIdsForFilters[0],
          carteiraFieldIds: carteiraFieldIdsForFilters,
          visibleColumns: appliedColumns,
          ignoredVisibleColumns: ignoredStatusVisibleColumnIds,
        },
      ),
      comprasEWorklogParentKeys,
    ),
    [activeCarteiraGroup, appliedColumns, comprasEWorklogParentKeys, rowFiltersForScope, carteiraFieldIdsForFilters, ignoredStatusVisibleColumnIds, issuesForMetrics, data?.issues],
  );

  const applyCostDecorationToState = useCallback((
    costResult: ProjectCostCalculationResponse,
  ) => {
    const jiraIssueByKey = buildJiraIssueByKey([
      ...(dataRef.current?.issues || []),
      ...(issuesForMetricsRef.current || []),
    ]);
    const { issueComputedMap, projectCostMap, projectSingleIssueMap } = buildIssueCostMapsFromAnalysis(costResult, jiraIssueByKey);
    const decorateRow = (row: JiraProjectIssueRow) => (
      decorateIssueRowWithCostMaps(row, issueComputedMap, projectCostMap, projectSingleIssueMap)
    );

    setData((prev) => {
      if (!prev?.issues?.length) return prev;
      const next = { ...prev, issues: prev.issues.map(decorateRow) };
      // Mantém o cache alinhado ao decorate — senão o próximo cache-hit apaga os custos.
      const activeKey = latestIssuesRequestKeyRef.current;
      if (activeKey && jiraQueryCacheRef.current[activeKey]) {
        jiraQueryCacheRef.current[activeKey] = {
          ...jiraQueryCacheRef.current[activeKey],
          data: next,
          costAnalysis: costResult,
        };
      }
      return next;
    });
    setIssuesForMetrics((prev) => (Array.isArray(prev) ? prev.map(decorateRow) : prev));
  }, []);

  const bumpCostPending = useCallback((target: 'visible' | 'full' | 'period', delta: number) => {
    if (target === 'visible') {
      activeVisibleCostRequestsRef.current = Math.max(0, activeVisibleCostRequestsRef.current + delta);
    } else if (target === 'full') {
      activeFullCostRequestsRef.current = Math.max(0, activeFullCostRequestsRef.current + delta);
    } else {
      activePeriodCostRequestsRef.current = Math.max(0, activePeriodCostRequestsRef.current + delta);
    }
    setPeriodCostCalculationPending(activePeriodCostRequestsRef.current > 0);
    setCostCalculationPending(
      activeVisibleCostRequestsRef.current > 0 || activeFullCostRequestsRef.current > 0,
    );
  }, []);

  const runCostCalculation = useCallback(async (params: {
    issueKeys: string[];
    scopeKey: string;
    scopeRef: React.MutableRefObject<string>;
    costSink: 'page' | 'executive' | 'period';
    batchSize?: number;
    timeoutMs?: number;
    maxSingleIssueRetries?: number;
    preferSingleRequest?: boolean;
    singleRequestTimeoutMs?: number;
    worklogStartDate?: string | null;
    worklogEndDate?: string | null;
    reportScope?: PeriodReportScopeApi;
    decorateTable?: boolean;
  }) => {
    const {
      issueKeys,
      scopeKey,
      scopeRef,
      costSink,
      batchSize = 50,
      timeoutMs = 45000,
      maxSingleIssueRetries = 2,
      preferSingleRequest = false,
      singleRequestTimeoutMs = 20000,
      worklogStartDate = null,
      worklogEndDate = null,
      reportScope = null,
      decorateTable = costSink !== 'period',
    } = params;
    const emptyForSink = (): ProjectCostCalculationResponse => emptyProjectCostResponse(appliedJql);
    const applyResultToSink = (result: ProjectCostCalculationResponse | null) => {
      if (costSink === 'period') setPeriodCostAnalysis(result);
      else if (costSink === 'executive') setCostAnalysis(result);
      else setPageCostAnalysis(result);
    };
    const exportJqlDiscovery = reportScope === 'planilha_export_worklogs' && !issueKeys.length;
    const tableCostAborted = () => (
      costSink !== 'period' && holdTableCostForExportRef.current
    );
    if (tableCostAborted()) {
      return;
    }
    if (!issueKeys.length && !exportJqlDiscovery) {
      applyResultToSink(emptyForSink());
      return;
    }
    if (scopeRef.current === scopeKey) {
      const cachedWhileSameScope = costScopeCacheRef.current[scopeKey];
      const staleExportEmpty = exportJqlDiscovery
        && cachedWhileSameScope
        && Number(cachedWhileSameScope.total_hours || 0) === 0
        && !(cachedWhileSameScope.issues?.length);
      if (!staleExportEmpty) return;
      delete costScopeCacheRef.current[scopeKey];
      scopeRef.current = '';
    }
    const cachedScopeResult = costScopeCacheRef.current[scopeKey];
    const staleExportEmptyCache = exportJqlDiscovery
      && cachedScopeResult
      && Number(cachedScopeResult.total_hours || 0) === 0
      && !(cachedScopeResult.issues?.length);
    if (cachedScopeResult && !staleExportEmptyCache) {
      scopeRef.current = scopeKey;
      applyResultToSink(cachedScopeResult);
      if (decorateTable) applyCostDecorationToState(cachedScopeResult);
      return;
    }
    if (staleExportEmptyCache) {
      delete costScopeCacheRef.current[scopeKey];
    }
    scopeRef.current = scopeKey;
    if (exportJqlDiscovery && costSink === 'period') {
      setPeriodCostAnalysis(null);
    }
    const pendingTarget = costSink === 'page' ? 'visible' : costSink === 'period' ? 'period' : 'full';

    if (costSink === 'period') {
      setPeriodCostError('');
    } else {
      setCostColumnError('');
      setCostAnalysisError('');
    }
    bumpCostPending(pendingTarget, 1);

    const onBatchMerged = costSink === 'period'
      ? (partial: ProjectCostCalculationResponse) => {
        if (scopeRef.current !== scopeKey) return;
        setPeriodCostAnalysis(partial);
      }
      : costSink === 'executive'
        ? (partial: ProjectCostCalculationResponse) => {
          if (scopeRef.current !== scopeKey) return;
          setCostAnalysis(partial);
        }
        : undefined;

    try {
      let result: ProjectCostCalculationResponse;
      let batchErrors: string[] = [];

      if (exportJqlDiscovery && costSink === 'period' && worklogStartDate && worklogEndDate) {
        try {
          result = await calculateComprasEExportHours(
            API_URL,
            worklogStartDate,
            worklogEndDate,
            FULL_WORKLOG_PERIOD_EXPORT_TIMEOUT_MS,
          );
        } catch {
          const fallback = await calculateProjectCostsBatched(
            API_URL,
            appliedJql,
            issueKeys,
            {
              batchSize,
              timeoutMs,
              includeSubtaskWorklogs: false,
              worklogStartDate,
              worklogEndDate,
              reportScope,
              maxSingleIssueRetries,
              shouldAbort: () => scopeRef.current !== scopeKey || tableCostAborted(),
              onBatchMerged,
            },
          );
          result = fallback.result;
          batchErrors = fallback.batchErrors;
        }
      } else if (preferSingleRequest) {
        try {
          const singleResponse = await axios.post<ProjectCostCalculationResponse>(
            `${API_URL}/api/projects/costs/calculate`,
            {
              jql: appliedJql,
              max_results: exportJqlDiscovery
                ? null
                : Math.max(1, Math.min(5000, issueKeys.length)),
              issue_keys: issueKeys,
              discover_export_scope: exportJqlDiscovery,
              include_subtask_worklogs: !exportJqlDiscovery,
              worklog_start_date: worklogStartDate,
              worklog_end_date: worklogEndDate,
              report_scope: reportScope,
            },
            { timeout: exportJqlDiscovery ? FULL_WORKLOG_PERIOD_EXPORT_TIMEOUT_MS : singleRequestTimeoutMs },
          );
          result = singleResponse.data;
        } catch {
          const fallback = await calculateProjectCostsBatched(
            API_URL,
            appliedJql,
            issueKeys,
            {
              batchSize,
              timeoutMs,
              includeSubtaskWorklogs: !exportJqlDiscovery,
              worklogStartDate,
              worklogEndDate,
              reportScope,
              maxSingleIssueRetries,
              shouldAbort: () => scopeRef.current !== scopeKey || tableCostAborted(),
              onBatchMerged,
            },
          );
          result = fallback.result;
          batchErrors = fallback.batchErrors;
        }
      } else {
        const fallback = await calculateProjectCostsBatched(
          API_URL,
          appliedJql,
          issueKeys,
          {
            batchSize,
            timeoutMs,
            includeSubtaskWorklogs: reportScope !== 'planilha_export_worklogs',
            worklogStartDate,
            worklogEndDate,
            reportScope,
            maxSingleIssueRetries,
            shouldAbort: () => scopeRef.current !== scopeKey || tableCostAborted(),
            onBatchMerged,
          },
        );
        result = fallback.result;
        batchErrors = fallback.batchErrors;
      }

      if (scopeRef.current !== scopeKey) return;
      if (batchErrors.length) {
        const partialMsg = `Calculo parcial de custos (${batchErrors.length} lote(s) com falha).`;
        if (costSink === 'period') {
          setPeriodCostError(partialMsg);
          applyResultToSink(null);
          return;
        }
        setCostColumnError(partialMsg);
      }
      const isEmptyExportResult = exportJqlDiscovery
        && Number(result.total_hours || 0) === 0
        && !(result.issues?.length);
      if (exportJqlDiscovery && result.export_scope_incomplete) {
        setPeriodCostError(
          'Cálculo do relatório Compras-e incompleto no Jira. HH no período indisponível — tente de novo em instantes.',
        );
        applyResultToSink(null);
        return;
      }
      if (!isEmptyExportResult) {
        storeCostScopeCache(scopeKey, result);
      }
      applyResultToSink(result);
      if (decorateTable) applyCostDecorationToState(result);
    } catch (costErr: unknown) {
      if (scopeRef.current !== scopeKey) return;
      scopeRef.current = '';
      const ax = costErr as {
        code?: string;
        message?: string;
        response?: { data?: { detail?: string } };
      };
      const timeoutDetail = ax.code === 'ECONNABORTED'
        ? 'Tempo limite ao calcular custos. A tabela foi carregada com dados Jira; tente novamente no botao Atualizar.'
        : '';
      const runtimeDetail = costErr instanceof Error ? costErr.message : (ax.message || '');
      const detail =
        timeoutDetail
        || ax.response?.data?.detail
        || runtimeDetail
        || 'Nao foi possivel carregar dados de receitas e custos para esta busca.';
      if (costSink !== 'period') {
        setCostColumnError(detail);
      }
      if (costSink === 'executive') {
        setCostAnalysisError(detail);
        setCostAnalysis(null);
      } else if (costSink === 'page') {
        setPageCostAnalysis(null);
      } else {
        setPeriodCostError(detail);
        setPeriodCostAnalysis(null);
      }
    } finally {
      bumpCostPending(pendingTarget, -1);
    }
  }, [appliedJql, applyCostDecorationToState, bumpCostPending, storeCostScopeCache]);

  const askNlAssistant = async () => {
    const question = nlQuestion.trim();
    if (!question) {
      setNlError('Digite uma pergunta para a IA.');
      return;
    }
    setNlLoading(true);
    setNlError('');
    try {
      const response = await axios.post<ProjectsNlQueryResponse>(
        `${API_URL}/api/projects/costs/nl-query`,
        {
          question,
          jira_jql_context: jqlInput,
          source_screen: 'projects_dashboard',
          max_jira_results: 25,
          max_db_rows: 50,
        }
      );
      setNlResult(response.data);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      setNlResult(null);
      setNlError(ax.response?.data?.detail || 'Não foi possível processar a pergunta.');
    } finally {
      setNlLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccess || !selectedViewId) return;
    if (!appliedJql.trim()) {
      setLoading(false);
      return;
    }
    if (useMetricsRowsForTable) return;
    // Refs evitam loop: fetchIssues/cursor mudam de identidade sem mudar a query.
    const currentCursor = cursorByStartAtRef.current[startAt] ?? null;
    void fetchIssuesRef.current({
      jql: appliedJql,
      selectedColumns: appliedColumns,
      startAt,
      pageSize,
      cursor: currentCursor,
    });
  }, [appliedColumns, appliedJql, canAccess, pageSize, selectedViewId, startAt, useMetricsRowsForTable]);

  useEffect(() => {
    if (!canAccess || !selectedViewId) return;
    if (!appliedJql.trim()) {
      setIssuesForMetrics([]);
      return;
    }
    if (!pmoFiltersActive) {
      return;
    }
    void fetchAllIssuesForMetrics({
      jql: appliedJql,
      selectedColumns: appliedColumns,
    });
  }, [appliedColumns, appliedJql, canAccess, fetchAllIssuesForMetrics, pmoFiltersActive, selectedViewId]);

  const toggleColumn = (columnId: string) => {
    setSelectedColumnsInput((prev) => (
      prev.includes(columnId)
        ? prev.filter((item) => item !== columnId)
        : [...prev, columnId]
    ));
  };

  const toggleKpi = (kpiId: string) => {
    setSelectedKpisInput((prev) => toggleKpiConfig(prev, kpiId));
  };

  const updateKpiTitle = (kpiId: string, title: string) => {
    setSelectedKpisInput((prev) => draftKpiTitle(prev, kpiId, title));
  };

  const commitKpiTitle = (kpiId: string, title: string) => {
    setSelectedKpisInput((prev) => commitKpiTitleValue(prev, kpiId, title));
  };

  const onKpiDragStart = (event: React.DragEvent<HTMLDivElement>, kpiId: string) => {
    setDraggedKpiId(kpiId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', kpiId);
  };

  const onKpiDrop = (event: React.DragEvent<HTMLDivElement>, targetId: string) => {
    event.preventDefault();
    const sourceId = draggedKpiId || event.dataTransfer.getData('text/plain');
    if (!sourceId || sourceId === targetId) {
      setDraggedKpiId(null);
      return;
    }
    setSelectedKpisInput((prev) => reorderKpiConfigs(prev, sourceId, targetId));
    setDraggedKpiId(null);
  };

  const startNewViewDraft = () => {
    const defaultKpis = DEFAULT_KPI_CARDS.map((id) => ({ id }));
    setSelectedViewId('');
    setViewNameInput('');
    setContextInput('');
    setJqlInput('');
    setSelectedColumnsInput(CORE_DEFAULT_COLUMNS);
    setSelectedKpisInput(defaultKpis);
    setSelectedColumnWidthsInput({});
    setAppliedColumns(CORE_DEFAULT_COLUMNS);
    setAppliedKpis(defaultKpis);
    setAppliedColumnWidths({});
    setSearchInput('');
    setAppliedSearch('');
    setAppliedJql('');
    setStartAt(0);
    setData(null);
    setIssuesForMetrics(null);
    issuesForMetricsScopeKeyRef.current = '';
    setCostAnalysis(null);
    setError('');
  };

  const saveCurrentAsNewView = async () => {
    const name = viewNameInput.trim();
    if (!name) {
      setError('Informe um nome para salvar a visão.');
      return;
    }
    const jql = jqlInput.trim();
    if (!jql) {
      setError('A JQL da visão é obrigatória para salvar.');
      return;
    }
    const normalizedColumns = normalizeSelectedColumns(selectedColumnsInput);
    if (!normalizedColumns.length) {
      setError('Selecione ao menos uma coluna para salvar a visão.');
      return;
    }

    const nextView: ProjectsSavedView = {
      id: createViewId(),
      name,
      context: contextInput.trim(),
      jql,
      selectedColumns: normalizedColumns,
      kpiConfigs: selectedKpisInput,
      columnWidths: filterWidthsByColumns(normalizedColumns, selectedColumnWidthsInput),
      isPreset: false,
    };
    const existingSameName = savedViews.find(
      (item) => normalizeMatchText(item.name) === normalizeMatchText(name),
    );
    if (existingSameName) {
      const confirmedOverwrite = await showConfirm({
        title: 'Visão já existe',
        message: `Já existe uma visão com o nome "${name}". Deseja sobrescrever?`,
        details: 'Se confirmar, as configurações atuais serão atualizadas com os dados da tela.',
        variant: 'warning',
        confirmLabel: 'Sobrescrever',
        cancelLabel: 'Cancelar',
      });
      if (!confirmedOverwrite) return;
    }
    setViewsSyncing(true);
    try {
      const persisted = await upsertGlobalViewByName(nextView);
      const nextViews = mergeViewsPreferringBackend([
        ...savedViews.filter((item) => normalizeMatchText(item.name) !== normalizeMatchText(persisted.name)),
        persisted,
      ]);
      setSavedViews(nextViews);
      applyView(persisted);
      setError('');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      setError(ax.response?.data?.detail || 'Erro ao salvar visão global.');
    } finally {
      setViewsSyncing(false);
    }
  };

  const duplicateCurrentView = async () => {
    if (!selectedView) {
      setError('Selecione uma visão para duplicar.');
      return;
    }
    const normalizedColumns = normalizeSelectedColumns(selectedColumnsInput);
    if (!normalizedColumns.length) {
      setError('Selecione ao menos uma coluna para duplicar a visão.');
      return;
    }
    const baseName = `${viewNameInput.trim() || selectedView.name} (cópia)`;
    const existingNames = new Set(savedViews.map((item) => item.name.trim().toLowerCase()));
    let nextName = baseName;
    let suffix = 2;
    while (existingNames.has(nextName.trim().toLowerCase())) {
      nextName = `${baseName} ${suffix}`;
      suffix += 1;
    }
    const nextView: ProjectsSavedView = {
      id: createViewId(),
      name: nextName,
      context: contextInput.trim(),
      jql: jqlInput.trim(),
      selectedColumns: normalizedColumns,
      kpiConfigs: selectedKpisInput.length ? selectedKpisInput : DEFAULT_KPI_CARDS.map((id) => ({ id })),
      columnWidths: filterWidthsByColumns(normalizedColumns, selectedColumnWidthsInput),
      isPreset: false,
    };
    setViewsSyncing(true);
    try {
      const persisted = await createGlobalView(nextView);
      const nextViews = [...savedViews.filter((item) => item.id !== persisted.id), persisted];
      setSavedViews(nextViews);
      applyView(persisted);
      setError('');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      setError(ax.response?.data?.detail || 'Erro ao duplicar visão global.');
    } finally {
      setViewsSyncing(false);
    }
  };

  const updateCurrentView = async () => {
    if (!selectedView) {
      setError('Selecione uma visão para atualizar.');
      return;
    }
    const nextName = viewNameInput.trim();
    if (!nextName) {
      setError('Informe um nome para atualizar a visão.');
      return;
    }
    const jql = jqlInput.trim();
    if (!jql) {
      setError('A JQL da visão é obrigatória para atualizar.');
      return;
    }
    const normalizedColumns = normalizeSelectedColumns(selectedColumnsInput);
    if (!normalizedColumns.length) {
      setError('Selecione ao menos uma coluna para atualizar a visão.');
      return;
    }
    const updatedView: ProjectsSavedView = {
      ...selectedView,
      name: nextName,
      context: contextInput.trim(),
      jql,
      selectedColumns: normalizedColumns,
      kpiConfigs: selectedKpisInput,
      columnWidths: filterWidthsByColumns(normalizedColumns, selectedColumnWidthsInput),
    };
    setViewsSyncing(true);
    try {
      const persisted = await upsertGlobalViewByName(updatedView);
      const nextViews = savedViews.map((item) => (item.id === selectedView.id ? persisted : item));
      setSavedViews(nextViews);
      applyView(persisted);
      setError('');
      setShowViewSavedIndicator(true);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      setError(ax.response?.data?.detail || 'Erro ao atualizar visão global.');
    } finally {
      setViewsSyncing(false);
    }
  };

  useEffect(() => {
    if (!showViewSavedIndicator) return;
    const timeoutId = window.setTimeout(() => {
      setShowViewSavedIndicator(false);
    }, 2500);
    return () => window.clearTimeout(timeoutId);
  }, [showViewSavedIndicator]);

  const deleteCurrentView = async () => {
    if (!selectedView) return;
    if (selectedView.isPreset) {
      setError('As visões padrão não podem ser excluídas.');
      return;
    }
    const confirmed = await showConfirm({
      title: 'Excluir visão',
      message: `Excluir a visão "${selectedView.name}"?`,
      details: 'Esta ação não pode ser desfeita.',
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;
    setViewsSyncing(true);
    try {
      await deleteGlobalView(selectedView);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      setError(ax.response?.data?.detail || 'Erro ao excluir visão global.');
      setViewsSyncing(false);
      return;
    }

    const nextViews = savedViews.filter((item) => item.id !== selectedView.id);
    if (!nextViews.length) {
      const defaultKpis = DEFAULT_KPI_CARDS.map((id) => ({ id }));
      setSavedViews([]);
      setSelectedViewId('');
      setViewNameInput('');
      setContextInput('');
      setJqlInput('');
      setSelectedColumnsInput(CORE_DEFAULT_COLUMNS);
      setSelectedKpisInput(defaultKpis);
      setSelectedColumnWidthsInput({});
      setAppliedColumns(CORE_DEFAULT_COLUMNS);
      setAppliedKpis(defaultKpis);
      setAppliedColumnWidths({});
      setSearchInput('');
      setAppliedSearch('');
      setAppliedJql('');
      setStartAt(0);
      setData(null);
      setCostAnalysis(null);
      setViewsSyncing(false);
      return;
    }
    setSavedViews(nextViews);
    applyView(nextViews[0]);
    setError('');
    setViewsSyncing(false);
  };

  const allColumnOptions = useMemo(() => {
    const core = Object.entries(CORE_COLUMN_LABELS).map(([id, label]) => ({
      id,
      label,
      field_type: 'core',
      is_custom: false,
    }));
    const localComputed = Object.entries(COMPUTED_COLUMN_LABELS).map(([id, label]) => ({
      id,
      label,
      field_type: 'computed',
      is_custom: false,
    }));
    const additional = availableColumns
      .filter((item) => !Object.prototype.hasOwnProperty.call(CORE_COLUMN_LABELS, item.id));
    return [...core, ...localComputed, ...additional];
  }, [availableColumns]);

  const filteredColumnOptions = useMemo(() => {
    const term = columnSearchInput.trim().toLowerCase();
    const base = term
      ? allColumnOptions.filter((column) => {
        const label = `${column.label}`.toLowerCase();
        const id = `${column.id}`.toLowerCase();
        return label.includes(term) || id.includes(term);
      })
      : allColumnOptions;

    return [...base].sort((a, b) => {
      const aSelected = selectedColumnsInput.includes(a.id) ? 1 : 0;
      const bSelected = selectedColumnsInput.includes(b.id) ? 1 : 0;
      if (aSelected !== bSelected) return bSelected - aSelected;
      return a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' });
    });
  }, [allColumnOptions, columnSearchInput, selectedColumnsInput]);

  const groupedColumnOptions = useMemo(() => {
    const grouped: Record<ProjectsColumnGroupId, ProjectsJiraColumnOption[]> = {
      jira_core: [],
      cost_computed: [],
      jira_custom: [],
    };
    filteredColumnOptions.forEach((column) => {
      const groupId = resolveColumnGroup(column.id);
      grouped[groupId].push(column);
    });
    return grouped;
  }, [filteredColumnOptions]);

  const columnLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    allColumnOptions.forEach((item) => {
      map[item.id] = item.label;
    });
    // Labels locais (PT) sempre prevalecem — evita cabeçalho cru tipo __sale_value_cents
    // quando o Jira/opções adicionais sobrescrevem o mesmo id.
    Object.entries(CORE_COLUMN_LABELS).forEach(([id, label]) => {
      map[id] = label;
    });
    Object.entries(COMPUTED_COLUMN_LABELS).forEach(([id, label]) => {
      map[id] = label;
    });
    if (!map.customfield_11263) {
      map.customfield_11263 = 'Observações / Plano de Ação';
    }
    return map;
  }, [allColumnOptions]);

  const validColumnIds = useMemo(() => {
    return new Set(allColumnOptions.map((item) => String(item.id || '').trim()).filter(Boolean));
  }, [allColumnOptions]);

  const observationColumnIds = useMemo(() => {
    const out = new Set<string>();
    // Campo canônico do PMO — garante editor mesmo se o label Jira variar.
    out.add('customfield_11263');
    availableColumns.forEach((item) => {
      const id = String(item.id || '').trim();
      const label = String(item.label || '').toLowerCase();
      if (id.startsWith('customfield_') && label.includes(OBSERVATION_LABEL_HINT)) {
        out.add(id);
      }
    });
    return out;
  }, [availableColumns]);

  const preferredObservationColumnId = useMemo(() => {
    const sorted = Array.from(observationColumnIds).sort((a, b) => a.localeCompare(b));
    return sorted[0] || '';
  }, [observationColumnIds]);

  // Observação NÃO é mais injetada automaticamente via useEffect:
  // isso gerava Maximum update depth (setState em cascata) e tela em branco.

  useEffect(() => {
    if (!canAccess || !user) return;
    if (!allColumnOptions.length) return;

    const findColumnByLabelTerms = (terms: string[]): string => {
      const normalizedTerms = terms.map((term) => normalizeMatchText(term));
      const match = allColumnOptions.find((column) => {
        const label = normalizeMatchText(String(column.label || ''));
        return normalizedTerms.every((term) => label.includes(term));
      });
      return String(match?.id || '');
    };

    const produtoCol = findColumnByLabelTerms(['produto']);
    const areaAtendimentoCol = findColumnByLabelTerms(['area', 'atendimento']) || findColumnByLabelTerms(['area']);
    const beneficiosCol = findColumnByLabelTerms(['beneficio']) || findColumnByLabelTerms(['beneficios']);
    const observacaoCol = preferredObservationColumnId || findColumnByLabelTerms(['observ']);
    const templateColumns = normalizeSelectedColumns([
      produtoCol,
      areaAtendimentoCol,
      'project',
      'hh_real_seconds',
      'go_live_real',
      'status',
      observacaoCol,
      beneficiosCol,
    ].filter(Boolean));

    setSavedViews((prev) => {
      const idx = prev.findIndex((view) => normalizeMatchText(view.name) === normalizeMatchText(INNOVATION_CONCLUDED_VIEW_NAME));
      // Não recria automaticamente a visão se ela tiver sido removida manualmente.
      if (idx < 0) return prev;

      const current = prev[idx];
      const mergedColumns = normalizeSelectedColumns([
        ...(current.selectedColumns || []),
        ...templateColumns,
      ]);
      if (areSameColumns(current.selectedColumns || [], mergedColumns)) {
        return prev;
      }
      const next = [...prev];
      next[idx] = {
        ...current,
        selectedColumns: mergedColumns,
      };
      return next;
    });
  }, [allColumnOptions, canAccess, filterWidthsByColumns, preferredObservationColumnId, user]);

  const observationDraftKey = useCallback((issueKey: string, fieldId: string): string => {
    return `${issueKey.trim().toUpperCase()}::${String(fieldId || '').trim()}`;
  }, []);

  const isObservationReadonlyError = useCallback((detail: string): boolean => {
    const text = (detail || '').toLowerCase();
    return text.includes('cannot be set')
      || text.includes('appropriate screen')
      || text.includes('unknown')
      || text.includes('campo de observação inválido');
  }, []);

  const saveInlineObservation = useCallback(async (issueKey: string, fieldId: string) => {
    if (!fieldId) {
      showAlert({
        title: 'Campo não encontrado',
        message: 'Não foi possível localizar o custom field de Observação no Jira.',
        variant: 'warning',
      });
      return;
    }
    const normalizedIssueKey = issueKey.trim().toUpperCase();
    if (!normalizedIssueKey) return;
    const draftKey = observationDraftKey(normalizedIssueKey, fieldId);
    const issue = (data?.issues || []).find((item) => item.key === normalizedIssueKey);
    const currentRendered = issue ? renderExtraFieldValue(issue.extra_fields?.[fieldId]) : '—';
    const currentValue = currentRendered === '—' ? '' : currentRendered;
    const nextValueRaw = observationDrafts[draftKey] ?? currentValue;
    const nextValueTrimmed = nextValueRaw.trim();
    if (nextValueTrimmed === currentValue.trim()) return;

    setSavingObservationKeys((prev) => ({ ...prev, [draftKey]: true }));
    try {
      const payload = { observation: nextValueTrimmed || null };
      const payloadWithField = {
        ...payload,
        field_id: fieldId || undefined,
      };
      await axios.put(
        `${API_URL}/api/projects/issues/${encodeURIComponent(normalizedIssueKey)}/observation`,
        payloadWithField,
      );
      const resolvedFieldId = fieldId;
      const finalValue = nextValueTrimmed;
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          issues: prev.issues.map((item) => {
            if (item.key !== normalizedIssueKey) return item;
            return {
              ...item,
              extra_fields: {
                ...(item.extra_fields || {}),
                [resolvedFieldId]: finalValue,
              },
            };
          }),
        };
      });
      const resolvedDraftKey = observationDraftKey(normalizedIssueKey, resolvedFieldId);
      setObservationDrafts((prev) => ({ ...prev, [resolvedDraftKey]: finalValue }));
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string } } };
      const detailMessage = ax.response?.data?.detail || 'Não foi possível salvar a observação no Jira.';
      if (isObservationReadonlyError(detailMessage)) {
        setReadonlyObservationKeys((prev) => ({ ...prev, [draftKey]: detailMessage }));
        showAlert({
          title: 'Campo somente leitura neste contexto',
          message: 'O Jira não permite editar este campo de observação para esta issue/visão. A célula ficará bloqueada para evitar novas tentativas.',
          variant: 'warning',
        });
        return;
      }
      showAlert({
        title: 'Erro',
        message: detailMessage,
        variant: 'danger',
      });
    } finally {
      setSavingObservationKeys((prev) => {
        if (!prev[draftKey]) return prev;
        const next = { ...prev };
        delete next[draftKey];
        return next;
      });
    }
  }, [data?.issues, isObservationReadonlyError, observationDraftKey, observationDrafts, showAlert]);

  const sortedIssues = useMemo(() => {
    const issues = [...pmoFilteredIssues];
    issues.sort((a, b) => {
      const getSort = (row: JiraProjectIssueRow): string | number => {
        if (Object.prototype.hasOwnProperty.call(CORE_COLUMN_LABELS, sortColumn)) {
          return getCoreSortValue(row, sortColumn as CoreColumnId);
        }
        if (COMPUTED_COLUMN_IDS.includes(sortColumn)) {
          const raw = row.extra_fields?.[sortColumn];
          const value = raw == null ? 0 : Number(raw);
          return Number.isFinite(value) ? value : 0;
        }
        const dynamicValue = row.extra_fields?.[sortColumn];
        return renderExtraFieldValue(dynamicValue).toLowerCase();
      };
      const av = getSort(a);
      const bv = getSort(b);
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else {
        cmp = String(av).localeCompare(String(bv), 'pt-BR', { sensitivity: 'base' });
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return issues;
  }, [pmoFilteredIssues, sortColumn, sortDirection]);

  const visibleColumns = useMemo(() => {
    const selected = appliedColumns.length ? appliedColumns : CORE_DEFAULT_COLUMNS;
    const sanitized = normalizeSelectedColumns(selected).filter((columnId) => {
      if (validColumnIds.has(columnId)) return true;
      if (columnId.startsWith('customfield_') || columnId.startsWith('__')) return true;
      if (CORE_COLUMN_IDS_SET.has(columnId as CoreColumnId)) return true;
      if (COMPUTED_COLUMN_IDS_SET.has(columnId)) return true;
      return false;
    });
    return sanitized.length ? sanitized : [...CORE_DEFAULT_COLUMNS];
  }, [appliedColumns, validColumnIds]);

  const syncTableScrollMetrics = useCallback(() => {
    const container = tableScrollRef.current;
    if (!container) {
      setTableScrollWidth((prev) => (prev === 0 ? prev : 0));
      setTableClientWidth((prev) => (prev === 0 ? prev : 0));
      return;
    }
    const nextScrollWidth = container.scrollWidth;
    const nextClientWidth = container.clientWidth;
    setTableScrollWidth((prev) => (prev === nextScrollWidth ? prev : nextScrollWidth));
    setTableClientWidth((prev) => (prev === nextClientWidth ? prev : nextClientWidth));
  }, []);

  useEffect(() => {
    syncTableScrollMetrics();
    const container = tableScrollRef.current;
    if (!container) return;
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => syncTableScrollMetrics());
      observer.observe(container);
      const tableEl = container.querySelector('table');
      if (tableEl) observer.observe(tableEl);
      return () => observer.disconnect();
    }
    const onResize = () => syncTableScrollMetrics();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [data?.issues, loading, syncTableScrollMetrics, visibleColumns]);

  const onTopScrollbarScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    if (scrollSyncLockRef.current) return;
    const target = event.currentTarget;
    const tableContainer = tableScrollRef.current;
    if (!tableContainer) return;
    scrollSyncLockRef.current = true;
    tableContainer.scrollLeft = target.scrollLeft;
    scrollSyncLockRef.current = false;
  }, []);

  const onTableScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    if (scrollSyncLockRef.current) return;
    const target = event.currentTarget;
    const topScrollbar = topScrollbarRef.current;
    if (!topScrollbar) return;
    scrollSyncLockRef.current = true;
    topScrollbar.scrollLeft = target.scrollLeft;
    scrollSyncLockRef.current = false;
  }, []);

  // Sanitização contínua de colunas via useEffect foi removida:
  // reescrever selected/applied columns a cada render causava loop infinito.

  const getColumnWidth = useCallback((columnId: string): number | undefined => {
    const width = appliedColumnWidths[columnId];
    if (typeof width === 'number' && width >= MIN_COLUMN_WIDTH_PX) return width;
    return getDefaultColumnWidth(columnId);
  }, [appliedColumnWidths]);

  const reorderColumns = useCallback((columns: string[], sourceId: string, targetId: string): string[] => {
    if (sourceId === targetId) return columns;
    const sourceIndex = columns.indexOf(sourceId);
    const targetIndex = columns.indexOf(targetId);
    if (sourceIndex < 0 || targetIndex < 0) return columns;
    const next = [...columns];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    return ensureSaleValueBeforeStatus(next);
  }, []);

  const moveColumnByDrag = useCallback((sourceId: string, targetId: string) => {
    setAppliedColumns((prev) => reorderColumns(prev, sourceId, targetId));
    setSelectedColumnsInput((prev) => reorderColumns(prev, sourceId, targetId));
  }, [reorderColumns]);

  const onHeaderDragStart = useCallback((event: React.DragEvent<HTMLTableHeaderCellElement>, columnId: string) => {
    if (resizingColumnId) return;
    setDraggedColumnId(columnId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', columnId);
  }, [resizingColumnId]);

  const onHeaderDrop = useCallback((event: React.DragEvent<HTMLTableHeaderCellElement>, targetColumnId: string) => {
    event.preventDefault();
    const sourceColumnId = draggedColumnId || event.dataTransfer.getData('text/plain');
    if (!sourceColumnId || sourceColumnId === targetColumnId) {
      setDraggedColumnId(null);
      return;
    }
    moveColumnByDrag(sourceColumnId, targetColumnId);
    setDraggedColumnId(null);
  }, [draggedColumnId, moveColumnByDrag]);

  const startColumnResize = useCallback((event: React.MouseEvent<HTMLSpanElement>, columnId: string) => {
    event.preventDefault();
    event.stopPropagation();
    const thElement = event.currentTarget.closest('th') as HTMLTableCellElement | null;
    const startWidth = getColumnWidth(columnId) ?? thElement?.offsetWidth ?? 220;
    resizeRef.current = {
      columnId,
      startX: event.clientX,
      startWidth,
    };
    setResizingColumnId(columnId);
  }, [getColumnWidth]);

  useEffect(() => {
    if (!resizingColumnId) return;
    const handleMouseMove = (event: MouseEvent) => {
      const resizeData = resizeRef.current;
      if (!resizeData) return;
      const deltaX = event.clientX - resizeData.startX;
      const nextWidth = Math.max(MIN_COLUMN_WIDTH_PX, Math.round(resizeData.startWidth + deltaX));
      setAppliedColumnWidths((prev) => ({ ...prev, [resizeData.columnId]: nextWidth }));
      setSelectedColumnWidthsInput((prev) => ({ ...prev, [resizeData.columnId]: nextWidth }));
    };
    const handleMouseUp = () => {
      setResizingColumnId(null);
      resizeRef.current = null;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumnId]);

  useEffect(() => {
    if (!visibleColumns.length) return;
    if (visibleColumns.includes(sortColumn)) return;
    const nextSort = visibleColumns[0] || 'updated';
    setSortColumn((prev) => (prev === nextSort ? prev : nextSort));
    setSortDirection((prev) => (prev === 'asc' ? prev : 'asc'));
  }, [sortColumn, visibleColumns]);

  const filteredIssues = useMemo(() => {
    const term = appliedSearch.trim();
    if (!term) return sortedIssues;

    return sortedIssues.filter((row) => (
      visibleColumns.some((columnId) => getSearchableColumnValue(row, columnId).includes(term))
    ));
  }, [appliedSearch, getSearchableColumnValue, sortedIssues, visibleColumns]);

  const jiraIssueByKeyForCost = useMemo(
    () => buildJiraIssueByKey([...(data?.issues || []), ...(issuesForMetrics || [])]),
    [data?.issues, issuesForMetrics],
  );

  const effectiveCostAnalysisForColumns = useMemo(
    () => (costAnalysis?.issues?.length ? costAnalysis : pageCostAnalysis),
    [costAnalysis, pageCostAnalysis],
  );

  const issueCostMapsFromAnalysis = useMemo(
    () => buildIssueCostMapsFromAnalysis(effectiveCostAnalysisForColumns, jiraIssueByKeyForCost),
    [effectiveCostAnalysisForColumns, jiraIssueByKeyForCost],
  );

  const decorateIssuesWithCost = useCallback((issues: JiraProjectIssueRow[]) => {
    if (!requiresCostColumns) return issues;
    if (!effectiveCostAnalysisForColumns?.issues?.length) return issues;
    const { issueComputedMap, projectCostMap, projectSingleIssueMap } = issueCostMapsFromAnalysis;
    return issues.map((row) => decorateIssueRowWithCostMaps(row, issueComputedMap, projectCostMap, projectSingleIssueMap));
  }, [effectiveCostAnalysisForColumns?.issues?.length, issueCostMapsFromAnalysis, requiresCostColumns]);

  const filteredIssuesWithCost = useMemo(
    () => decorateIssuesWithCost(filteredIssues),
    [decorateIssuesWithCost, filteredIssues],
  );

  const metricsScopeRows = useMemo(() => {
    const hasSearchFilter = Boolean(appliedSearch.trim());
    const hasAnyFilter = pmoFiltersActive || hasSearchFilter;
    if (hasAnyFilter) return filteredIssuesWithCost;
    return decorateIssuesWithCost(pmoFilteredIssuesForMetrics);
  }, [appliedSearch, decorateIssuesWithCost, filteredIssuesWithCost, pmoFilteredIssuesForMetrics, pmoFiltersActive]);

  const carteiraTotals = useMemo(() => {
    if (!showCarteiraBreakdown || !metricsScopeRows.length) return null;
    return computeCarteiraTotals(
      metricsScopeRows,
      EMPTY_STRUCTURED_FILTERS,
      activeCarteiraGroup,
      {
        carteiraFieldId: carteiraFieldIdsForFilters[0],
        carteiraFieldIds: carteiraFieldIdsForFilters,
        visibleColumns: appliedColumns,
        ignoredVisibleColumns: ignoredStatusVisibleColumnIds,
      },
    );
  }, [activeCarteiraGroup, appliedColumns, carteiraFieldIdsForFilters, ignoredStatusVisibleColumnIds, metricsScopeRows, showCarteiraBreakdown]);

  const pagedIssues = useMemo(() => {
    if (!useMetricsRowsForTable) return filteredIssuesWithCost;
    return filteredIssuesWithCost.slice(startAt, startAt + pageSize);
  }, [filteredIssuesWithCost, pageSize, startAt, useMetricsRowsForTable]);

  const visibleIssueKeysForCost = useMemo(
    () => normalizeIssueKeysForCost(pagedIssues.map((issue) => issue.key)),
    [pagedIssues],
  );
  const visibleIssueKeysForCostKey = useMemo(
    () => visibleIssueKeysForCost.join('|'),
    [visibleIssueKeysForCost],
  );
  /** Escopo de custo dos KPIs: com filtro PMO, só as issues que passaram no filtro (carteira/período/etc.). */
  const kpiCostScopeRows = useMemo(() => {
    if (pmoFiltersActive) return pmoFilteredIssuesForMetrics;
    return issuesForMetrics || data?.issues || [];
  }, [data?.issues, issuesForMetrics, pmoFilteredIssuesForMetrics, pmoFiltersActive]);
  const fullIssueKeysForCost = useMemo(
    () => normalizeIssueKeysForCost(kpiCostScopeRows.map((issue) => issue.key)),
    [kpiCostScopeRows],
  );
  const fullIssueKeysForCostKey = useMemo(
    () => fullIssueKeysForCost.join('|'),
    [fullIssueKeysForCost],
  );
  const pmoFilterScopeKey = useMemo(
    () => JSON.stringify({
      ...appliedStructuredFilters,
      carteiraGroup: activeCarteiraGroup,
      search: appliedSearch,
    }),
    [activeCarteiraGroup, appliedSearch, appliedStructuredFilters],
  );

  // Fase 1: custos da página visível — só quando a tabela tem colunas/KPIs que precisam de custo.
  useEffect(() => {
    if (!canAccess || !selectedViewId || !appliedJql.trim()) return;
    if (!requiresExecutiveCostData) return;
    if (exportPeriodNotSettled) return;
    if (!visibleIssueKeysForCost.length) return;
    const visibleScopeKey = buildVisibleCostScopeKey({
      jql: appliedJql,
      startAt,
      pageSize,
      issueKeys: visibleIssueKeysForCost,
    });
    void runCostCalculation({
      issueKeys: visibleIssueKeysForCost,
      scopeKey: visibleScopeKey,
      scopeRef: latestVisibleCostScopeKeyRef,
      costSink: 'page',
      batchSize: VISIBLE_COST_BATCH_SIZE,
      timeoutMs: VISIBLE_COST_BATCH_TIMEOUT_MS,
      maxSingleIssueRetries: VISIBLE_COST_MAX_SINGLE_RETRIES,
      preferSingleRequest: true,
      singleRequestTimeoutMs: VISIBLE_COST_SINGLE_TIMEOUT_MS,
      ...TABLE_COST_PIPELINE_DEFAULTS,
      decorateTable: requiresCostColumns,
    });
  }, [
    appliedJql,
    canAccess,
    pageSize,
    requiresCostColumns,
    requiresExecutiveCostData,
    runCostCalculation,
    selectedViewId,
    startAt,
    visibleIssueKeysForCost,
    visibleIssueKeysForCostKey,
    exportPeriodNotSettled,
  ]);

  // Fase 2: busca o escopo completo em paralelo (não bloqueia no cache da página).
  // A trava anterior impedia KPIs/colunas de avançarem quando o cálculo visível
  // falhava ou quando o effect não reexecutava após o cache ser preenchido.
  useEffect(() => {
    if (!canAccess || !selectedViewId || !appliedJql.trim()) return;
    if (!requiresCostData) return;
    if (!data?.issues?.length) return;
    void fetchAllIssuesForMetrics({
      jql: appliedJql,
      selectedColumns: appliedColumns,
    });
  }, [appliedColumns, appliedJql, canAccess, data?.issues?.length, fetchAllIssuesForMetrics, requiresCostData, selectedViewId]);

  // Fase 3a: cálculo full executivo (colunas/KPIs financeiros) — sem filtro de worklog no período.
  useEffect(() => {
    if (!canAccess || !selectedViewId || !appliedJql.trim()) return;
    if (!requiresExecutiveCostData) return;
    if (exportPeriodNotSettled) return;
    if (!fullIssueKeysForCost.length) return;
    const metricsScopeKey = buildMetricsRowsCacheKey(appliedJql, appliedColumns);
    if (!pmoFiltersActive && issuesForMetricsScopeKeyRef.current !== metricsScopeKey) return;
    const fullScopeKey = buildFullCostScopeKey({
      jql: appliedJql,
      issueKeys: fullIssueKeysForCost,
      filterScope: pmoFilterScopeKey,
      worklogStartDate: null,
      worklogEndDate: null,
    });
    void runCostCalculation({
      issueKeys: fullIssueKeysForCost,
      scopeKey: fullScopeKey,
      scopeRef: latestCostScopeKeyRef,
      costSink: 'executive',
      preferSingleRequest: false,
      ...TABLE_COST_PIPELINE_DEFAULTS,
      decorateTable: requiresCostColumns,
    });
  }, [
    appliedColumns,
    appliedJql,
    buildMetricsRowsCacheKey,
    canAccess,
    fullIssueKeysForCost,
    fullIssueKeysForCostKey,
    pmoFilterScopeKey,
    pmoFiltersActive,
    requiresCostColumns,
    requiresExecutiveCostData,
    exportPeriodNotSettled,
    runCostCalculation,
    selectedViewId,
  ]);

  // Fase 3b: KPIs de HH no período — pipeline isolado; não recalcula colunas da tabela.
  useEffect(() => {
    if (!canAccess || !selectedViewId || !appliedJql.trim()) return;
    if (!requiresWorklogPeriodCost) return;
    const exportWorklogPeriodScope = appliedPeriodReportScopeApi === 'planilha_export_worklogs';
    const periodIssueKeys = exportWorklogPeriodScope ? [] : fullIssueKeysForCost;
    if (!exportWorklogPeriodScope) {
      if (!periodIssueKeys.length) return;
      const metricsScopeKey = buildMetricsRowsCacheKey(appliedJql, appliedColumns);
      if (!pmoFiltersActive && issuesForMetricsScopeKeyRef.current !== metricsScopeKey) return;
    }
    const periodScopeKey = buildFullCostScopeKey({
      jql: appliedJql,
      issueKeys: periodIssueKeys,
      filterScope: pmoFilterScopeKey,
      worklogStartDate: pmoWorklogPeriod.worklogStartDate,
      worklogEndDate: pmoWorklogPeriod.worklogEndDate,
      reportScope: appliedPeriodReportScopeApi,
      exportJqlDiscovery: exportWorklogPeriodScope,
    });
    void runCostCalculation({
      issueKeys: periodIssueKeys,
      scopeKey: periodScopeKey,
      scopeRef: latestPeriodCostScopeKeyRef,
      costSink: 'period',
      batchSize: FULL_WORKLOG_PERIOD_BATCH_SIZE,
      timeoutMs: FULL_WORKLOG_PERIOD_BATCH_TIMEOUT_MS,
      maxSingleIssueRetries: FULL_WORKLOG_PERIOD_MAX_SINGLE_RETRIES,
      preferSingleRequest: exportWorklogPeriodScope,
      worklogStartDate: pmoWorklogPeriod.worklogStartDate,
      worklogEndDate: pmoWorklogPeriod.worklogEndDate,
      reportScope: appliedPeriodReportScopeApi,
      ...PERIOD_EXPORT_PIPELINE_DEFAULTS,
    });
  }, [
    appliedColumns,
    appliedJql,
    appliedPeriodReportScopeApi,
    buildMetricsRowsCacheKey,
    canAccess,
    fullIssueKeysForCost,
    fullIssueKeysForCostKey,
    pmoFilterScopeKey,
    pmoFiltersActive,
    pmoWorklogPeriod.worklogEndDate,
    pmoWorklogPeriod.worklogStartDate,
    requiresWorklogPeriodCost,
    runCostCalculation,
    selectedViewId,
  ]);

  const hasKnownTotal = typeof data?.total === 'number' && Number.isFinite(data.total);
  const totalRecords = useMemo(() => {
    if (useMetricsRowsForTable) return filteredIssues.length;
    if (hasKnownTotal) return Number(data?.total || 0);
    return startAt + filteredIssues.length + (Boolean(data?.has_more) ? 1 : 0);
  }, [data?.has_more, data?.total, filteredIssues.length, hasKnownTotal, startAt, useMetricsRowsForTable]);

  const totalPages = useMemo(() => {
    if (!totalRecords) return 1;
    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }, [pageSize, totalRecords]);

  const currentPage = useMemo(() => Math.floor(startAt / pageSize) + 1, [startAt, pageSize]);

  useEffect(() => {
    if (hasKnownTotal) {
      if (startAt < totalRecords) return;
      if (!totalRecords) {
        if (startAt !== 0) setStartAt(0);
        return;
      }
      const lastPageStart = Math.max(0, (Math.ceil(totalRecords / pageSize) - 1) * pageSize);
      if (startAt !== lastPageStart) setStartAt(lastPageStart);
      return;
    }
    if (filteredIssues.length > 0) return;
    if (startAt !== 0) setStartAt(Math.max(0, startAt - pageSize));
  }, [filteredIssues.length, hasKnownTotal, pageSize, startAt, totalRecords]);

  const kpiCards = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    let hhVendidoSeconds = 0;
    let hhRealSeconds = 0;
    let concluidoCount = 0;
    let atrasadosCount = 0;
    let riscoCount = 0;
    let pontosCriticosCount = 0;
    let fasesNoPrazoCount = 0;
    let projetosForaDoPrazoCount = 0;

    metricsScopeRows.forEach((row) => {
      const vendido = Number(row.hh_vendido_seconds || 0);
      const real = Number(row.hh_real_seconds || 0);
      hhVendidoSeconds += Number.isFinite(vendido) ? vendido : 0;
      hhRealSeconds += Number.isFinite(real) ? real : 0;

      const done = isDoneStatusLabel(row.status || '');
      if (done) concluidoCount += 1;
      const riskLevel = executiveRiskLevel(row, {
        visibleColumns: appliedColumns,
        ignoredVisibleColumns: ignoredStatusVisibleColumnIds,
      });
      if (riskLevel === 'critico') pontosCriticosCount += 1;
      if (riskLevel === 'critico' || riskLevel === 'em_risco') riscoCount += 1;
      if (!done && riskLevel === 'no_prazo') fasesNoPrazoCount += 1;
      if (isDeliveryOutOfSchedule(row)) projetosForaDoPrazoCount += 1;

      if (!done && row.duedate) {
        const due = new Date(row.duedate);
        due.setHours(0, 0, 0, 0);
        if (!Number.isNaN(due.getTime()) && due < now) {
          atrasadosCount += 1;
        }
      }
    });

    const hhVendido = hhVendidoSeconds / 3600;
    const hhReal = hhRealSeconds / 3600;
    const consumoPercent = hhVendido > 0 ? (hhReal / hhVendido) * 100 : null;
    const scopedIssueKeys = new Set(metricsScopeRows.map((row) => String(row.key || '').trim().toUpperCase()).filter(Boolean));
    const exportWorklogPeriodScope = isExportWorklogPeriodScope(appliedStructuredFilters.reportScope);
    const filterCostIssuesToScope = (
      issues: ProjectCostCalculationResponse['issues'] | undefined,
    ) => (
      (issues || []).filter((issue) => (
        scopedIssueKeys.has(String(issue.issue_key || '').trim().toUpperCase())
      ))
    );
    const worklogCostIssueCount = periodCostAnalysis?.issues?.length ?? 0;
    const hasPartialWorklogCost = worklogCostIssueCount > 0 || Number(periodCostAnalysis?.total_hours || 0) > 0;
    const analysisForExecutiveKpi = costAnalysis?.issues?.length ? costAnalysis : pageCostAnalysis;
    const analysisForWorklogKpi = requiresWorklogPeriodCost
      ? (hasPartialWorklogCost ? periodCostAnalysis : null)
      : (costAnalysis?.issues?.length ? costAnalysis : null);
    const scopedCostIssues = filterCostIssuesToScope(analysisForExecutiveKpi?.issues);
    const scopedWorklogCostIssues = exportWorklogPeriodScope
      ? (analysisForWorklogKpi?.issues || [])
      : filterCostIssuesToScope(analysisForWorklogKpi?.issues);
    const totalCommercialMarginCents = metricsScopeRows.reduce((acc, row) => {
      const value = Number(row.extra_fields?.[COMMERCIAL_MARGIN_COLUMN_ID] || 0);
      return acc + (Number.isFinite(value) ? value : 0);
    }, 0);
    const totalEstimatedCostCents = metricsScopeRows.reduce((acc, row) => {
      const value = Number(row.extra_fields?.[ESTIMATED_COST_COLUMN_ID] || 0);
      return acc + (Number.isFinite(value) ? value : 0);
    }, 0);
    const totalSaleFromRows = metricsScopeRows.reduce((acc, row) => {
      const value = Number(row.extra_fields?.[SALE_VALUE_COLUMN_ID] || 0);
      return acc + (Number.isFinite(value) ? value : 0);
    }, 0);
    const totalCostFromRows = metricsScopeRows.reduce((acc, row) => {
      const value = Number(row.extra_fields?.[ISSUE_COST_COLUMN_ID] || 0);
      return acc + (Number.isFinite(value) ? value : 0);
    }, 0);
    const criticalOsCount = scopedCostIssues.filter((issue) => {
      const demandType = String(issue.demand_type || '').trim().toUpperCase();
      if (demandType !== 'OS') return false;
      const revenue = Number(issue.revenue_cents || 0);
      const cost = Number(issue.cost_cents || 0);
      if (revenue <= 0) return false;
      const marginPct = ((revenue - cost) / revenue) * 100;
      return marginPct <= criticalMarginThresholdPct;
    }).length;
    const criticalByDemandType = scopedCostIssues.reduce<Record<string, number>>((acc, issue) => {
      const demandType = String(issue.demand_type || '').trim().toUpperCase() || 'OUTROS';
      const revenue = Number(issue.revenue_cents || 0);
      const cost = Number(issue.cost_cents || 0);
      if (revenue <= 0) return acc;
      const marginPct = ((revenue - cost) / revenue) * 100;
      if (marginPct > criticalMarginThresholdPct) return acc;
      acc[demandType] = Number(acc[demandType] || 0) + 1;
      return acc;
    }, {});
    const criticalDemandTop = Object.entries(criticalByDemandType).sort((a, b) => b[1] - a[1])[0];
    const criticalDemandLabel = criticalDemandTop ? `${criticalDemandTop[1].toLocaleString('pt-BR')} ${criticalDemandTop[0]}` : '0';
    const hasCostBase = scopedCostIssues.length > 0 || totalSaleFromRows > 0 || totalCostFromRows > 0;
    const costsStillLoading = requiresExecutiveCostData && !hasCostBase && !exportPeriodNotSettled && (costCalculationPending || loadingMetricsIssues);
    const moneyOrLoading = (cents: number): string => {
      if (hasCostBase) return formatCurrencyCompactFromCents(cents);
      if (costsStillLoading) return 'Calculando…';
      if (costColumnError || costAnalysisError) return 'Indisponível';
      return '—';
    };
    const totalOwnHours = scopedCostIssues.reduce((acc, issue) => acc + Number(issue.own_hours || 0), 0);
    const totalThirdHours = scopedCostIssues.reduce((acc, issue) => acc + Number(issue.third_party_hours || 0), 0);
    const totalHoursForMix = totalOwnHours + totalThirdHours;
    const ownHoursPct = totalHoursForMix > 0 ? (totalOwnHours / totalHoursForMix) * 100 : null;
    const thirdHoursPct = totalHoursForMix > 0 ? (totalThirdHours / totalHoursForMix) * 100 : null;
    const totalOwnCost = scopedCostIssues.reduce((acc, issue) => acc + Number(issue.own_cost_cents || 0), 0);
    const totalThirdCost = scopedCostIssues.reduce((acc, issue) => acc + Number(issue.third_party_cost_cents || 0), 0);
    const totalCostForMix = totalOwnCost + totalThirdCost;
    const ownCostPct = totalCostForMix > 0 ? (totalOwnCost / totalCostForMix) * 100 : null;
    const thirdCostPct = totalCostForMix > 0 ? (totalThirdCost / totalCostForMix) * 100 : null;
    const totalRevenueCents = scopedCostIssues.length
      ? scopedCostIssues.reduce((acc, issue) => acc + Number(issue.revenue_cents || 0), 0)
      : totalSaleFromRows;
    const totalCostCentsScoped = scopedCostIssues.length
      ? scopedCostIssues.reduce((acc, issue) => acc + Number(issue.cost_cents || 0), 0)
      : totalCostFromRows;
    const totalMarginCents = totalRevenueCents - totalCostCentsScoped;
    const totalRealHours = exportWorklogPeriodScope
      ? Number(analysisForWorklogKpi?.total_hours || 0)
      : scopedWorklogCostIssues.reduce((acc, issue) => acc + Number(issue.hours || 0), 0);
    // Na metodologia do relatório os HH saem com 2 casas, iguais aos da tela Compras-e.
    const periodHoursFractionDigits = exportWorklogPeriodScope ? 2 : 1;
    const formatPeriodHours = (value: number): string => value.toLocaleString('pt-BR', {
      minimumFractionDigits: periodHoursFractionDigits,
      maximumFractionDigits: periodHoursFractionDigits,
    });
    const hhRealPeriodValue = formatPeriodHours(totalRealHours);
    const hhRealWorklogLoading = requiresWorklogPeriodCost && !hasPartialWorklogCost && periodCostCalculationPending;
    const periodKpiUnavailable = Boolean(periodCostError);
    const hhToBill = Math.max(0, totalRealHours - hhVendido);
    const avgRevenuePerHourCents = totalRealHours > 0 ? (totalRevenueCents / totalRealHours) : 0;
    const estimatedSaleToBillCents = Math.round(hhToBill * avgRevenuePerHourCents);
    const totalMarginPct = totalRevenueCents > 0 ? (totalMarginCents / totalRevenueCents) * 100 : null;
    const formatPairPct = (first: number | null, second: number | null): string => {
      if (costsStillLoading) return 'Calculando…';
      if (first == null || second == null) return '—';
      return `${first.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}% / ${second.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}%`;
    };

    const issuesWithWorklogInPeriod = new Set(
      scopedWorklogCostIssues
        .filter((issue) => Number(issue.hours || 0) > 0)
        .map((issue) => {
          const parentKey = String(issue.parent_issue_key || issue.issue_key || '').trim().toUpperCase();
          return parentKey;
        }),
    );
    let hhVendidoRelatorioSeconds = 0;
    const exportScopeSoldHours = exportWorklogPeriodScope
      ? Number(analysisForWorklogKpi?.export_scope_sold_hours ?? NaN)
      : NaN;
    if (Number.isFinite(exportScopeSoldHours)) {
      hhVendidoRelatorioSeconds = exportScopeSoldHours * 3600;
    } else {
      metricsScopeRows.forEach((row) => {
        const key = String(row.key || '').trim().toUpperCase();
        if (!issuesWithWorklogInPeriod.has(key)) return;
        const vendido = Number(row.hh_vendido_seconds || 0);
        hhVendidoRelatorioSeconds += Number.isFinite(vendido) ? vendido : 0;
      });
    }
    const hhVendidoRelatorio = hhVendidoRelatorioSeconds / 3600;
    const consumoRelatorioPercent = hhVendidoRelatorio > 0 ? (totalRealHours / hhVendidoRelatorio) * 100 : null;
    const periodKpiNeedsRange = !pmoWorklogPeriodActive ? '—' : null;

    // Nesta visão a contagem só fecha depois que o relatório devolve quem apontou no período.
    const comprasERowScopePending = isComprasEWorklogView && comprasEWorklogParentKeys == null;

    const valueById: Record<string, string> = {
      total_items: comprasERowScopePending
        ? (periodKpiUnavailable ? 'Indisponível' : 'Calculando…')
        : metricsScopeRows.length.toLocaleString('pt-BR'),
      hh_vendido_total: hhVendido.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      hh_real_total: hhReal.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      hh_real_worklog_period: !pmoWorklogPeriodActive
        ? '—'
        : hhRealWorklogLoading
          ? 'Calculando…'
          : periodKpiUnavailable
            ? 'Indisponível'
            : hhRealPeriodValue,
      hh_vendido_relatorio: periodKpiNeedsRange || (
        hhRealWorklogLoading
          ? 'Calculando…'
          : periodKpiUnavailable
            ? 'Indisponível'
            : formatPeriodHours(hhVendidoRelatorio)
      ),
      consumo_hh_relatorio_percent: periodKpiNeedsRange || (
        hhRealWorklogLoading
          ? 'Calculando…'
          : periodKpiUnavailable
            ? 'Indisponível'
            : consumoRelatorioPercent != null
              ? `${consumoRelatorioPercent.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
              : '—'
      ),
      consumo_hh_percent: consumoPercent == null
        ? '—'
        : `${consumoPercent.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`,
      concluidos: concluidoCount.toLocaleString('pt-BR'),
      atrasados_due_date: atrasadosCount.toLocaleString('pt-BR'),
      em_risco: riscoCount.toLocaleString('pt-BR'),
      pontos_criticos: pontosCriticosCount.toLocaleString('pt-BR'),
      fases_no_prazo: fasesNoPrazoCount.toLocaleString('pt-BR'),
      projetos_fora_do_prazo: projetosForaDoPrazoCount.toLocaleString('pt-BR'),
      venda_total: moneyOrLoading(totalRevenueCents),
      venda_a_faturar: moneyOrLoading(estimatedSaleToBillCents),
      hh_a_faturar: hasCostBase
        ? hhToBill.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
        : (costsStillLoading ? 'Calculando…' : '—'),
      margem_na_venda_percentual_total: hasCostBase && totalMarginPct != null
        ? `${totalMarginPct.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
        : (costsStillLoading ? 'Calculando…' : '—'),
      hh_realizado_total: hasCostBase
        ? totalRealHours.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
        : (costsStillLoading ? 'Calculando…' : '—'),
      margem_venda_comercial_total: moneyOrLoading(totalCommercialMarginCents),
      custo_previsto_total: moneyOrLoading(totalEstimatedCostCents),
      custo_total: moneyOrLoading(totalCostCentsScoped),
      horas_realizadas_mix: hasCostBase ? formatPairPct(ownHoursPct, thirdHoursPct) : formatPairPct(null, null),
      custo_mix: hasCostBase ? formatPairPct(ownCostPct, thirdCostPct) : formatPairPct(null, null),
      margem_pos_custo_total: moneyOrLoading(totalMarginCents),
      ponto_critico_os: hasCostBase
        ? `${criticalOsCount.toLocaleString('pt-BR')} OS`
        : (costsStillLoading ? 'Calculando…' : '—'),
      ponto_critico_demanda: hasCostBase ? criticalDemandLabel : (costsStillLoading ? 'Calculando…' : '—'),
    };

    return displayKpiConfigs
      .map((config) => {
        const base = KPI_OPTIONS_BY_ID[config.id];
        if (!base) return null;
        return {
          ...base,
          label: config.title?.trim() || base.label,
          description: base.id === 'total_items' && isComprasEWorklogView
            ? 'Demandas que tiveram alguma hora de trabalho lançada no período escolhido em De/Até — mesmo recorte do card "Demandas com apontamento" da tela Compras-e. Demandas sem nenhuma hora no período não entram nesta conta.'
            : base.description,
          value: valueById[base.id] || '—',
        };
      })
      .filter((item): item is ProjectsKpiOption & { value: string } => Boolean(item));
  }, [
    appliedColumns,
    comprasEWorklogParentKeys,
    isComprasEWorklogView,
    costAnalysis,
    costAnalysisError,
    costCalculationPending,
    periodCostAnalysis,
    periodCostCalculationPending,
    periodCostError,
    costColumnError,
    criticalMarginThresholdPct,
    displayKpiConfigs,
    ignoredStatusVisibleColumnIds,
    loadingMetricsIssues,
    metricsScopeRows,
    pageCostAnalysis,
    pmoWorklogPeriodActive,
    appliedStructuredFilters.reportScope,
    exportPeriodNotSettled,
    requiresExecutiveCostData,
    requiresWorklogPeriodCost,
  ]);

  const onSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  };

  const sortIndicator = (column: SortColumn): string => {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const canGoNext = useMetricsRowsForTable
    ? Boolean(startAt + pageSize < totalRecords)
    : (hasKnownTotal
      ? Boolean(data && startAt + pageSize < totalRecords)
      : Boolean(hasMoreByStartAt[startAt] ?? data?.has_more));

  const goToPage = (page: number) => {
    const total = totalRecords;
    if (!total) return;
    const bounded = Math.min(Math.max(1, page), totalPages);
    setStartAt((bounded - 1) * pageSize);
  };

  const renderPaginationControls = () => (
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
      <span className="small text-muted">
        {pmoFiltersActive
          ? `${filteredIssues.length.toLocaleString('pt-BR')} registro(s) após filtros PMO (página ${currentPage} de ${totalPages})`
          : hasKnownTotal
            ? `${totalRecords.toLocaleString('pt-BR')} registro(s) — página ${currentPage} de ${totalPages}`
            : `${Math.max(startAt + filteredIssues.length, filteredIssues.length).toLocaleString('pt-BR')} registro(s) carregado(s) — página ${currentPage}`}
      </span>
      <div className="d-flex gap-2 align-items-center flex-wrap">
        <div className="d-flex align-items-center gap-1">
          <span className="small text-muted">Por página</span>
          <Form.Select
            size="sm"
            style={{ width: 90 }}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setStartAt(0);
              setCursorByStartAt({ 0: null });
              setHasMoreByStartAt({});
            }}
            disabled={loading}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </div>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={startAt <= 0 || loading}
          onClick={() => goToPage(1)}
        >
          Primeira
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={startAt <= 0 || loading}
          onClick={() => setStartAt(Math.max(0, startAt - pageSize))}
        >
          Anterior
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={!canGoNext || loading}
          onClick={() => setStartAt(startAt + pageSize)}
        >
          Próxima
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={!hasKnownTotal || !canGoNext || loading}
          onClick={() => goToPage(totalPages)}
        >
          Última
        </Button>
      </div>
    </div>
  );

  if (!user) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!canAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <ProjectAreaLayout className="projects-dashboard-page">
      <ProjectPageHeader
        title="Área de Projetos"
        subtitle="Visão gerencial integrada com Jira para acompanhamento de contexto, custo e evolução."
        contextLabel="Dados sincronizados com Jira"
        contextBadge="Projetos"
        actions={[
          {
            id: 'refresh-projects',
            label: 'Atualizar',
            onClick: () => {
              if (!appliedJql.trim()) {
                setError('A JQL da visão é obrigatória para carregar dados.');
                return;
              }
              jiraQueryCacheRef.current = {};
              metricsRowsCacheRef.current = {};
              issuesForMetricsScopeKeyRef.current = '';
              resetCostScopeRuntime(true);
              setCursorByStartAt({ 0: null });
              setHasMoreByStartAt({});
              setStartAt(0);
              setIssuesForMetrics(null);
              setCostAnalysis(null);
              void fetchIssues({
                jql: appliedJql,
                selectedColumns: appliedColumns,
                startAt: 0,
                pageSize,
                cursor: null,
              });
              if (pmoFiltersActive || requiresCostData) {
                void fetchAllIssuesForMetrics({
                  jql: appliedJql,
                  selectedColumns: appliedColumns,
                });
              }
            },
            disabled: loading,
          },
        ]}
      />

      <ProjectsAlertStack jiraConfigured={Boolean(data?.jira_configured ?? true)} error={error} warning={costColumnError} />

      <Row className="g-3 projects-dashboard-workspace">
        {!isSidebarCollapsed && (
        <Col xxl={3} lg={4} className="projects-dashboard-sidebar">
          <ProjectSectionCard
            title="Visões salvas"
            headerRight={(
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted">{savedViews.length} visão(ões)</small>
                {canManageViews && (
                  <>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => void exportGlobalViews()}
                      disabled={viewsSyncing}
                    >
                      Exportar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => viewsImportInputRef.current?.click()}
                      disabled={viewsSyncing}
                    >
                      Importar
                    </Button>
                  </>
                )}
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setIsSidebarCollapsed(true)}
                >
                  Minimizar
                </Button>
              </div>
            )}
            bodyClassName="p-2"
          >
              <input
                ref={viewsImportInputRef}
                type="file"
                accept="application/json,.json"
                style={{ display: 'none' }}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  void importGlobalViewsFromFile(file);
                }}
              />
              <ListGroup variant="flush" className="projects-view-menu">
                {savedViews.map((view) => (
                  <ListGroup.Item
                    key={view.id}
                    action
                    active={view.id === selectedViewId}
                    onClick={() => applyView(view)}
                    className="projects-view-menu-item"
                  >
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      <span>{view.name}</span>
                      {view.isPreset && <Badge bg="info">PMO</Badge>}
                    </div>
                    {view.context && (
                      <div className="small mt-1 text-wrap">{view.context}</div>
                    )}
                    <div className="small mt-1 text-muted projects-view-jql" title={view.jql || 'não definida'}>
                      JQL: <code>{view.jql || 'não definida'}</code>
                    </div>
                    <div className="small text-muted">
                      Colunas: <strong>{view.selectedColumns?.length || 0}</strong>
                    </div>
                    <div className="small text-muted">
                      KPIs: <strong>{view.kpiConfigs?.length || 0}</strong>
                    </div>
                  </ListGroup.Item>
                ))}
                {!savedViews.length && (
                  <div className="small text-muted px-2 py-2">
                    Nenhuma visão/contexto criado ainda.
                  </div>
                )}
              </ListGroup>
              {canManageViews && (
                <div className="d-grid mt-2">
                  <Button variant="outline-primary" onClick={startNewViewDraft} disabled={viewsSyncing}>
                    Criar nova visão
                  </Button>
                </div>
              )}
          </ProjectSectionCard>

          {canManageViews && (
          <ProjectSectionCard
            className="mt-3"
            title="Editor de visão"
            headerRight={<small className="text-muted">Consulta JQL e Copiloto IA</small>}
          >
              <Tabs
                id="projects-save-panel-tabs"
                activeKey={savePanelTab}
                onSelect={(key) => setSavePanelTab((key as 'jql' | 'ai') || 'jql')}
                className="mb-3"
              >
                <Tab eventKey="jql" title="Consulta JQL" />
                <Tab eventKey="ai" title="Copiloto IA" />
              </Tabs>

              {savePanelTab === 'jql' ? (
                <>
                  <Form.Group className="mb-2">
                    <Form.Label className="small text-muted mb-1">Nome da visão</Form.Label>
                    <Form.Control
                      type="text"
                      value={viewNameInput}
                      onChange={(e) => setViewNameInput(e.target.value)}
                      placeholder="Ex.: Projetos críticos"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-muted mb-1">Contexto</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={contextInput}
                      onChange={(e) => setContextInput(e.target.value)}
                      placeholder="Descreva o objetivo dessa visão"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-muted mb-1">JQL da visão (obrigatória)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={jqlInput}
                      onChange={(e) => setJqlInput(e.target.value)}
                      placeholder='Ex.: project = Portal ORDER BY updated DESC'
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-muted mb-1">Colunas da tabela</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      className="mb-2"
                      value={columnSearchInput}
                      onChange={(e) => setColumnSearchInput(e.target.value)}
                      placeholder="Buscar coluna..."
                    />
                    <div className="projects-columns-picker">
                      {COLUMN_GROUP_ORDER.map((groupId) => {
                        const columns = groupedColumnOptions[groupId];
                        if (!columns?.length) return null;
                        const selectedInGroup = columns.filter((column) => selectedColumnsInput.includes(column.id)).length;
                        const groupMeta = COLUMN_GROUP_META[groupId];
                        return (
                          <div key={groupId} className="projects-columns-group">
                            <div className="projects-columns-group-header">
                              <div className="fw-semibold small">{groupMeta.label}</div>
                              <Badge bg="secondary">{selectedInGroup}/{columns.length}</Badge>
                            </div>
                            <div className="small text-muted mb-1">{groupMeta.hint}</div>
                            {columns.map((column) => (
                              <Form.Check
                                key={column.id}
                                type="checkbox"
                                id={`projects-col-${column.id}`}
                                label={`${column.label}${column.is_custom ? ' (custom)' : ''}`}
                                checked={selectedColumnsInput.includes(column.id)}
                                onChange={() => toggleColumn(column.id)}
                              />
                            ))}
                          </div>
                        );
                      })}
                      {!filteredColumnOptions.length && (
                        <div className="small text-muted">Nenhuma coluna encontrada.</div>
                      )}
                    </div>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-muted mb-1">Cards KPI (acima da busca)</Form.Label>
                    <div className="projects-columns-picker">
                      {KPI_OPTIONS.map((kpi) => (
                        <Form.Check
                          key={kpi.id}
                          type="checkbox"
                          id={`projects-kpi-${kpi.id}`}
                          label={`${kpi.label} — ${kpi.description}`}
                          checked={selectedKpisInput.some((item) => item.id === kpi.id)}
                          onChange={() => toggleKpi(kpi.id)}
                        />
                      ))}
                    </div>
                    {!!selectedKpisInput.length && (
                      <div className="projects-kpi-selected-list mt-2">
                        {selectedKpisInput.map((kpiConfig) => {
                          const option = KPI_OPTIONS_BY_ID[kpiConfig.id];
                          if (!option) return null;
                          return (
                            <div
                              key={kpiConfig.id}
                              className={`projects-kpi-selected-item${draggedKpiId === kpiConfig.id ? ' is-dragging' : ''}`}
                              onDragOver={(event) => event.preventDefault()}
                              onDrop={(event) => onKpiDrop(event, kpiConfig.id)}
                            >
                              <div
                                className="projects-kpi-selected-handle"
                                title="Arraste para reordenar"
                                draggable
                                onDragStart={(event) => onKpiDragStart(event, kpiConfig.id)}
                                onDragEnd={() => setDraggedKpiId(null)}
                              >
                                ⋮⋮
                              </div>
                              <div className="projects-kpi-selected-content">
                                <div className="small fw-semibold">{option.label}</div>
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  value={kpiConfig.title ?? ''}
                                  onChange={(event) => updateKpiTitle(kpiConfig.id, event.target.value)}
                                  onBlur={(event) => commitKpiTitle(kpiConfig.id, event.target.value)}
                                  onKeyDown={(event) => {
                                    event.stopPropagation();
                                    if (event.key === 'Enter') {
                                      event.currentTarget.blur();
                                    }
                                  }}
                                  onPointerDown={(event) => event.stopPropagation()}
                                  placeholder="Título personalizado (opcional)"
                                  autoComplete="off"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Form.Group>
                  <div className="d-grid gap-2 projects-save-actions">
                    <Button variant="success" onClick={applyFilters} disabled={loading}>
                      Executar consulta
                    </Button>
                    <Button variant="primary" onClick={() => void saveCurrentAsNewView()} disabled={viewsSyncing}>
                      Salvar nova visão
                    </Button>
                    <Button variant="outline-secondary" onClick={() => void duplicateCurrentView()} disabled={!selectedView || viewsSyncing}>
                      Duplicar visão atual
                    </Button>
                    <Button variant="outline-primary" onClick={() => void updateCurrentView()} disabled={!selectedView || viewsSyncing}>
                      Atualizar visão atual
                    </Button>
                    <Button variant="outline-danger" onClick={() => void deleteCurrentView()} disabled={Boolean(selectedView?.isPreset) || viewsSyncing}>
                      Excluir visão atual
                    </Button>
                  </div>
                  {selectedView?.isPreset && (
                    <div className="small text-muted mt-2">Visões padrão não podem ser excluídas.</div>
                  )}
                  {showViewSavedIndicator && (
                    <Alert variant="success" className="py-2 mb-0 mt-2">
                      Visão atualizada e salva com sucesso.
                    </Alert>
                  )}
                </>
              ) : (
                <div>
                  <Form.Group className="mb-2">
                    <Form.Label className="small text-muted mb-1">Perguntar à IA (Jira + banco)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={nlQuestion}
                      onChange={(e) => setNlQuestion(e.target.value)}
                      placeholder="Ex.: Quais projetos têm maior custo e risco no período atual?"
                    />
                  </Form.Group>
                  <div className="d-grid mb-2">
                    <Button variant="outline-primary" onClick={askNlAssistant} disabled={nlLoading}>
                      {nlLoading ? 'Consultando...' : 'Perguntar à IA'}
                    </Button>
                  </div>
                  {nlError && <Alert variant="danger" className="py-2 mb-2">{nlError}</Alert>}
                  {nlResult && (
                    <>
                      <Alert variant="info" className="py-2 mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                        {nlResult.answer_text}
                      </Alert>
                      {nlResult.jira?.used_jql && (
                        <div className="small mb-2">
                          <div className="text-muted mb-1">JQL usada/sugerida:</div>
                          <code>{nlResult.jira.used_jql}</code>
                          <div className="d-grid mt-1">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => {
                                setJqlInput(nlResult.jira?.used_jql || '');
                                setSavePanelTab('jql');
                              }}
                            >
                              Aplicar JQL e voltar para Consulta JQL
                            </Button>
                          </div>
                        </div>
                      )}
                      {!!nlResult.warnings?.length && (
                        <Alert variant="warning" className="py-2 mb-0">
                          {nlResult.warnings.join(' | ')}
                        </Alert>
                      )}
                    </>
                  )}
                </div>
              )}
          </ProjectSectionCard>
          )}
        </Col>
        )}

        <Col xxl={isSidebarCollapsed ? 12 : 9} lg={isSidebarCollapsed ? 12 : 8}>
          <div className="projects-dashboard-main-flow">
          {isSidebarCollapsed && (
            <div className="d-flex justify-content-end">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setIsSidebarCollapsed(false)}
              >
                Mostrar menu lateral de visões
              </Button>
            </div>
          )}
          <Card className="dashboard-section-card dashboard-filters-card mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-semibold">Filtros PMO</span>
                    {pmoFiltersActive && (
                      <Badge bg="primary" pill>
                        {pmoFilteredIssues.length.toLocaleString('pt-BR')} de {((useMetricsRowsForTable ? issuesForMetrics?.length : data?.issues?.length) || 0).toLocaleString('pt-BR')} no escopo
                      </Badge>
                    )}
                  </div>
                  <small className="text-muted">
                    Filtros estruturados PMO (período, carteira, classificação, status, vigência, fase). Atuam na tabela e nos KPIs em tempo real e não alteram a JQL.
                    Período filtra a issue por datas (criação/go-live); os R$ vêm do cálculo de worklogs das issues filtradas — enquanto calcula aparece &quot;Calculando…&quot;, não &quot;R$ 0&quot;.
                    {isPresetViewActive ? ` Visão: ${selectedView?.name}` : ' Selecione uma visão PMO na barra lateral para KPIs/colunas específicos.'}
                  </small>
                  {isAdmin && (
                    <small className="d-block text-muted mt-1">
                      Admin: o filtro de Carteira usa o campo &quot;Classificação do Atendimento&quot; (customfield_11434). Se a classificação estiver preenchida, só entra a carteira correspondente — sem fallback pelo nome &quot;Esteira Portal de Compras&quot;. Fallback por resumo/projeto só quando a classificação estiver vazia. Compras-E: projeto CMPE/COMPRAS-E (tudo), classificação COMPRAS-E, ou tag literal [Compras-e] no Portal. Sem a tag, demanda classificada como Portal de Compras não entra.
                    </small>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  {!isPmoFiltersCollapsed && (
                    <Button variant="primary" size="sm" onClick={applyStructuredFilters} disabled={loading}>
                      Aplicar filtros
                    </Button>
                  )}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setIsPmoFiltersCollapsed((prev) => !prev)}
                  >
                    {isPmoFiltersCollapsed ? 'Expandir filtros' : 'Minimizar filtros'}
                  </Button>
                </div>
              </div>
              {!isPmoFiltersCollapsed ? (
                <ProjectsStructuredFilters
                  value={structuredFilters}
                  carteiraGroup={activeCarteiraGroup}
                  onChange={handleStructuredFiltersChange}
                  disabled={loading}
                  enabledPeriodKpiIds={extractPeriodKpiIds(selectedKpisInput)}
                  onTogglePeriodKpi={toggleKpi}
                />
              ) : (
                <small className="text-muted">
                  Filtros PMO minimizados. Clique em "Expandir filtros" para ajustar período, carteira, status, vigência e fase.
                </small>
              )}
            </Card.Body>
          </Card>

          <ProjectsKpiMetricGrid
            items={kpiCards.map((kpi) => ({
              id: kpi.id,
              label: kpi.label,
              value: kpi.value,
              help: kpi.description,
            }))}
          />

          {showCarteiraBreakdown && (
            <Card className="dashboard-section-card mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="fw-semibold">
                    Totalizadores por carteira
                    {loadingMetricsIssues && <small className="text-muted ms-2">(atualizando base completa...)</small>}
                  </div>
                </div>
                {carteiraTotals ? (
                  <Row className="g-2">
                    {carteiraTotals.items.map((item) => (
                      <Col key={item.carteira_id} md={4} lg={3}>
                        <div className="border rounded p-2 h-100">
                          <div className="small text-muted">{item.carteira_label}</div>
                          <div className="fw-semibold">{item.total_issues.toLocaleString('pt-BR')} projetos</div>
                          <div className="small">
                            HH vendido: {item.hh_vendido_hours.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h
                          </div>
                          <div className="small">
                            HH real: {item.hh_real_hours.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h
                          </div>
                          <div className="small">
                            % Consumo HH: {formatHhConsumptionPercent(item.hh_real_hours, item.hh_vendido_hours)}
                          </div>
                        </div>
                      </Col>
                    ))}
                    <Col md={4} lg={3}>
                      <div className="border border-primary rounded p-2 h-100 bg-light">
                        <div className="small text-muted">{carteiraTotals.geral.carteira_label}</div>
                        <div className="fw-semibold">{carteiraTotals.geral.total_issues.toLocaleString('pt-BR')} projetos</div>
                        <div className="small">
                          HH vendido: {carteiraTotals.geral.hh_vendido_hours.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h
                        </div>
                        <div className="small">
                          HH real: {carteiraTotals.geral.hh_real_hours.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h
                        </div>
                        <div className="small">
                          % Consumo HH: {formatHhConsumptionPercent(carteiraTotals.geral.hh_real_hours, carteiraTotals.geral.hh_vendido_hours)}
                        </div>
                      </div>
                    </Col>
                  </Row>
                ) : (
                  <div className="small text-muted">
                    Sem dados de carteira para os filtros atuais.
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          <Card className="dashboard-section-card dashboard-filters-card mb-3">
            <Card.Body>
              <Row className="g-2 align-items-end">
                <Col md={10}>
                  <Form.Label className="small text-muted mb-1">Busca rápida nos resultados (resumo/chave)</Form.Label>
                  <Form.Control
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Ex.: OS-140 ou termo no resumo"
                  />
                </Col>
                <Col md={2} className="d-grid">
                  <Button variant="primary" onClick={applyFilters} disabled={loading}>
                    Buscar
                  </Button>
                </Col>
              </Row>
              <div className="small text-muted">
                JQL aplicada no backend: <code>{data?.applied_jql || '—'}</code>{' '}
                {selectedView && !selectedView.jql.trim() && '(preencha a JQL da visão para iniciar)'}
              </div>
            </Card.Body>
          </Card>

          <Card className="dashboard-section-card projects-results-card">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                <span>{selectedView?.name || 'Acompanhamento de projetos (Jira)'}</span>
                <span className="small fw-normal d-inline-flex align-items-center gap-2 flex-wrap">
                  {costCalculationPending && !exportPeriodNotSettled && (
                    <span className="projects-cost-pending-chip" aria-live="polite">
                      <Spinner animation="border" size="sm" className="projects-cost-pending-spinner" />
                      Calculando custos…
                    </span>
                  )}
                  {periodCostCalculationPending && requiresWorklogPeriodCost && (
                    <span className="projects-cost-pending-chip" aria-live="polite">
                      <Spinner animation="border" size="sm" className="projects-cost-pending-spinner" />
                      Calculando HH no período…
                    </span>
                  )}
                  Registros na tela: <strong>{pagedIssues.length}</strong>
                  {filteredIssues.length !== pmoFilteredIssues.length && (
                    <> (de {filteredIssues.length.toLocaleString('pt-BR')} após busca local)</>
                  )}
                </span>
              </div>
              {selectedView?.context && <div className="small fw-normal mt-1 text-muted">{selectedView.context}</div>}
              {!!costColumnError && (
                <div className="small text-danger mt-1" role="alert">
                  Custos: {costColumnError}
                </div>
              )}
              {!!periodCostError && (
                <div className="small text-danger mt-1" role="alert">
                  HH no período: {periodCostError}
                </div>
              )}
            </Card.Header>
            {data && totalRecords > 0 && (
              <Card.Body className="py-2 border-bottom">
                {renderPaginationControls()}
              </Card.Body>
            )}
            {loading && !data ? (
              <div className="projects-loading-state px-3 py-4">
                <ProjectLoadingState message="Sincronizando dados do Jira e preparando análise..." />
              </div>
            ) : (
              <>
                {visibleColumns.includes('status') && (
                  <div className="projects-phase-color-legend px-3 py-2 border-bottom" role="note" aria-label="Legenda das cores da coluna Fase">
                    <span className="projects-phase-color-legend-title">Cores da Fase</span>
                    <span className="projects-phase-color-legend-hint text-muted">
                      O texto é o status do Jira; a cor indica o risco da entrega.
                    </span>
                    <div className="projects-phase-color-legend-items">
                      {PHASE_COLOR_LEGEND.map((item) => (
                        <span key={item.bg} className="projects-phase-color-legend-item" title={item.description}>
                          <Badge bg={item.bg} className="fw-normal">{item.label}</Badge>
                          <span className="text-muted">{item.description}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div
                  ref={topScrollbarRef}
                  className="projects-top-horizontal-scrollbar"
                  onScroll={onTopScrollbarScroll}
                  style={{ display: tableScrollWidth > tableClientWidth ? 'block' : 'none' }}
                >
                  <div style={{ width: tableScrollWidth }} />
                </div>
                <div ref={tableScrollRef} className="projects-table-scroll-container" onScroll={onTableScroll}>
                  <Table striped bordered hover className="mb-0 align-middle projects-resizable-table dashboard-table">
                  <colgroup>
                    {visibleColumns.map((columnId) => (
                      <col key={`col-${columnId}`} style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px` } : undefined} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      {visibleColumns.map((columnId) => (
                        <th
                          key={columnId}
                          draggable={canAdjustTableLayout && !resizingColumnId}
                          onDragStart={(event) => {
                            if (!canAdjustTableLayout) return;
                            onHeaderDragStart(event, columnId);
                          }}
                          onDragOver={(event) => {
                            if (!canAdjustTableLayout) return;
                            event.preventDefault();
                          }}
                          onDrop={(event) => {
                            if (!canAdjustTableLayout) return;
                            onHeaderDrop(event, columnId);
                          }}
                          onDragEnd={() => setDraggedColumnId(null)}
                          className={`projects-draggable-header${draggedColumnId === columnId ? ' is-dragging' : ''}`}
                          style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px`, minWidth: `${getColumnWidth(columnId)}px` } : undefined}
                        >
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 fw-bold text-body text-decoration-none"
                            onClick={() => onSort(columnId)}
                          >
                            {COMPUTED_COLUMN_LABELS[columnId] || columnLabelMap[columnId] || columnId}{sortIndicator(columnId)}
                          </Button>
                          {canAdjustTableLayout && (
                            <span
                              className="projects-column-resize-handle"
                              onMouseDown={(event) => startColumnResize(event, columnId)}
                              title="Arraste para ajustar largura"
                            />
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {!pagedIssues.length ? (
                      <tr>
                        <td colSpan={Math.max(1, visibleColumns.length)} className="py-2">
                          <ProjectEmptyState
                            title="Nenhum registro encontrado"
                            description={
                              pmoFiltersActive
                                ? 'Nenhuma linha passou pelos filtros PMO ativos. Ajuste período, carteira, status, vigência ou fase — ou clique em Limpar filtros.'
                                : 'Revise a JQL da visão, ajuste os filtros rápidos ou selecione outro contexto salvo.'
                            }
                            actionLabel={canManageViews ? 'Criar nova visão' : undefined}
                            onAction={canManageViews ? startNewViewDraft : undefined}
                          />
                        </td>
                      </tr>
                    ) : (
                      pagedIssues.map((row) => (
                        <tr key={row.key}>
                          {visibleColumns.map((columnId) => {
                            if (columnId === 'project') {
                              return (
                                <td key={`${row.key}-${columnId}`} className="projects-cell-project" style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px` } : undefined}>
                                  <a href={row.jira_browse_url} target="_blank" rel="noopener noreferrer" className="projects-project-link">
                                    <strong className="projects-project-summary">{row.summary || row.key}</strong>
                                  </a>
                                  <div className="small text-muted text-truncate" title={row.key}>{row.key}</div>
                                  <div className="small text-truncate" title={`${row.project_key} — ${row.project_name}`}>
                                    {row.project_key} — {row.project_name}
                                  </div>
                                </td>
                              );
                            }
                            if (columnId === 'issue_type') return <td key={`${row.key}-${columnId}`} style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px` } : undefined}>{row.issue_type || '—'}</td>;
                            if (columnId === 'go_live_planned') return <td key={`${row.key}-${columnId}`} style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px` } : undefined}>{formatDateBR(row.go_live_planned)}</td>;
                            if (columnId === 'go_live_real') return <td key={`${row.key}-${columnId}`} style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px` } : undefined}>{formatDateBR(row.go_live_real)}</td>;
                            if (columnId === 'updated') return <td key={`${row.key}-${columnId}`} style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px` } : undefined}>{formatDateBR(row.updated)}</td>;
                            if (columnId === 'created') return <td key={`${row.key}-${columnId}`} style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px` } : undefined}>{formatDateBR(row.created)}</td>;
                            if (columnId === 'duedate') return <td key={`${row.key}-${columnId}`} style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px` } : undefined}>{formatDateBR(row.duedate)}</td>;
                            if (columnId === 'hh_vendido_seconds') return <td key={`${row.key}-${columnId}`} style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px` } : undefined}>{formatHoursFromSeconds(row.hh_vendido_seconds)}</td>;
                            if (columnId === 'hh_real_seconds') return <td key={`${row.key}-${columnId}`} style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px` } : undefined}>{formatHoursFromSeconds(row.hh_real_seconds)}</td>;
                            if (columnId === 'status') {
                              return (
                                <td key={`${row.key}-${columnId}`} style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px` } : undefined}>
                                  <Badge
                                    bg={phaseVariant(row, appliedColumns, ignoredStatusVisibleColumnIds)}
                                    className="fw-normal"
                                    title={phaseRiskTitle(row, appliedColumns, ignoredStatusVisibleColumnIds)}
                                  >
                                    {row.status || '—'}
                                  </Badge>
                                </td>
                              );
                            }
                            if (COMPUTED_COLUMN_IDS.includes(columnId)) {
                              const rawValue = row.extra_fields?.[columnId];
                              const waitingCostValue = (
                                rawValue == null
                                && !exportPeriodNotSettled
                                && (
                                  costCalculationPending
                                  || (
                                    requiresCostColumns
                                    && !effectiveCostAnalysisForColumns?.issues?.length
                                    && !costColumnError
                                    && pagedIssues.length > 0
                                  )
                                )
                              );
                              return (
                                <td key={`${row.key}-${columnId}`} className="text-end" style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px` } : undefined}>
                                  {renderComputedCostCellValue(columnId, rawValue, waitingCostValue)}
                                </td>
                              );
                            }
                            if (observationColumnIds.has(columnId)) {
                              const draftKey = observationDraftKey(row.key, columnId);
                              const observationValue = renderExtraFieldValue(row.extra_fields?.[columnId]);
                              const baseValue = observationValue === '—' ? '' : observationValue;
                              const draftValue = observationDrafts[draftKey] ?? baseValue;
                              const isSaving = Boolean(savingObservationKeys[draftKey]);
                              const readonlyReason = !canEditIssueObservation
                                ? 'Apenas administradores podem editar observações.'
                                : (readonlyObservationKeys[draftKey] || (!columnId ? 'Campo não encontrado' : ''));
                              const isReadonly = Boolean(readonlyReason);
                              return (
                                <td key={`${row.key}-${columnId}`} className="projects-observation-cell" style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px` } : undefined}>
                                  <div className={`projects-observation-editor${isSaving ? ' is-saving' : ''}${isReadonly ? ' is-readonly' : ''}`}>
                                    <Form.Control
                                      as="textarea"
                                      rows={3}
                                      className="projects-observation-input"
                                      value={draftValue}
                                      disabled={isSaving || isReadonly}
                                      placeholder={isReadonly ? 'Somente leitura neste contexto' : 'Observações / plano de ação…'}
                                      onChange={(event) => {
                                        const next = event.target.value;
                                        setObservationDrafts((prev) => ({ ...prev, [draftKey]: next }));
                                      }}
                                      onBlur={() => {
                                        if (isReadonly) return;
                                        void saveInlineObservation(row.key, columnId);
                                      }}
                                      onKeyDown={(event) => {
                                        if (isReadonly) return;
                                        if (event.key === 'Enter' && !event.shiftKey) {
                                          event.preventDefault();
                                          (event.currentTarget as HTMLTextAreaElement).blur();
                                        }
                                      }}
                                    />
                                    <div className="projects-observation-hint">
                                      {isReadonly
                                        ? readonlyReason
                                        : (isSaving
                                          ? (
                                            <>
                                              <Spinner animation="border" size="sm" className="me-1" />
                                              Salvando…
                                            </>
                                          )
                                          : 'Ao sair deste campo, a observação é salva automaticamente.')}
                                    </div>
                                  </div>
                                </td>
                              );
                            }
                            return <td key={`${row.key}-${columnId}`} style={getColumnWidth(columnId) ? { width: `${getColumnWidth(columnId)}px` } : undefined}>{renderExtraFieldValue(row.extra_fields?.[columnId])}</td>;
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                  </Table>
                </div>
                {data && totalRecords > 0 && (
                  <Card.Footer>{renderPaginationControls()}</Card.Footer>
                )}
              </>
            )}
          </Card>
          </div>
        </Col>
      </Row>

    </ProjectAreaLayout>
  );
};

export default ProjectsDashboard;
